import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useVirtualizer } from '@tanstack/react-virtual';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import CustomDropdown from './ui/CustomDropdown';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { getDeviceFp } from '../utils/deviceFp';
import {
  Play,
  Pause,
  Download,
  Loader2,
  Settings,
  ChevronDown,
  ArrowRight,
  Clock,
  FileAudio,
  Volume2,
  Check,
  BadgeCheck,
  Search,
  Mic,
  Mic2,
  Zap,
  Upload,
  FileText,
  Trash2,
  X,
  RotateCcw,
  Sparkles,
  Heart,
  Package,
  CheckSquare,
  Square,
  Save,
  Copy,
  Info,
  Type,
  BookOpen,
  LayoutGrid,
  Columns3,
  PanelBottom,
  Pencil,
  ShieldAlert
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AudioTextModal from './ui/AudioTextModal';
import SrtEditorModal from './audio/SrtEditorModal';
import VoiceCardCompact from './voice/VoiceCardCompact';
import VoiceLibraryModal from './voice/VoiceLibraryModal';
import VoiceVariationModal from './voice/VoiceVariationModal';
import LanguageFilterDropdown from './voice/LanguageFilterDropdown';
import LanguagePreviewDropdown from './voice/LanguagePreviewDropdown';
import FlagIcon from './voice/FlagIcon';
import { VoicePreviewProvider, useVoicePreview } from '../contexts/VoicePreviewContext';
import VoicePreviewBar from './voice/VoicePreviewBar';
import LanguageListPopover from './voice/LanguageListPopover';
import DarkTooltip from './ui/DarkTooltip';
import {
  getPreviewUrl,
  getPrimaryFlagCode,
  getPrimaryLanguageName,
  getCountryName,
  uniqueLanguages,
  translate,
  LANGUAGE_PT,
  absolutizeVoicePreviews,
  matchesSearch,
  getLanguagesOfVoice,
  getFlagCode,
} from '../utils/voiceMetadata';
import { getVoiceBackground } from '../utils/voiceBackground';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════
// 3D SPHERE AVATAR — unique hue per voice name, glass-like sphere effect
// ═══════════════════════════════════════════════════════════════════════════
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < (str || '').length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
};

const get3DSphereStyle = (voiceName) => {
  const hue = hashString(voiceName) % 360;
  return {
    hue,
    gradient: [
      `radial-gradient(circle at 30% 25%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.12) 18%, transparent 45%)`,
      `radial-gradient(circle at 70% 72%, rgba(0,0,0,0.25) 0%, transparent 50%)`,
      `linear-gradient(135deg, hsl(${hue}, 72%, 62%) 0%, hsl(${hue}, 66%, 50%) 50%, hsl(${hue}, 58%, 34%) 100%)`,
    ].join(', '),
    dotGradient: `linear-gradient(135deg, hsl(${hue}, 72%, 62%), hsl(${hue}, 58%, 34%))`,
  };
};

const VoiceAvatar = ({ voice, size = 32, isPreviewing = false, onPlayClick }) => {
  const initial = (voice?.name || '?').charAt(0).toUpperCase();
  const sphere = get3DSphereStyle(voice?.name || '?');
  // Background determinístico do CDN R2 — mesma voz sempre mesma imagem.
  // Usa voice_info.voice_id (ElevenLabs) com fallback pro voice.id (UUID Carlos).
  const bgVoiceId = (voice && voice.voice_info && voice.voice_info.voice_id) || (voice && voice.id) || null;
  const bgUrl = bgVoiceId ? getVoiceBackground(bgVoiceId) : null;
  // Estado da imagem: começa "loading" (esfera visível, letra escondida); ao
  // carregar com sucesso, imagem cobre; em erro, letra aparece (fallback).
  const [imgStatus, setImgStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'
  const showLetter = imgStatus === 'error';

  // ITER: checkmark de qualidade (estilo ElevenLabs) no canto sup-direito.
  // - professional → dourado (#FFB300)
  // - high_quality → escuro (#1F2937)
  const category = (voice?.voice_info?.category || '').toLowerCase();
  const isProfessional = category === 'professional';
  const isHighQuality = category === 'high_quality';
  const showCheck = isProfessional || isHighQuality;
  // Tamanho do badge: piso de 14×14 (lista lateral size=30) — escala suave
  // pra avatares maiores (size=44 → 15, size=56 → 20). Estilo verificado IG/X.
  const checkSize = Math.max(14, Math.round(size * 0.35));

  return (
    // Outer wrapper: SEM overflow:hidden — pra checkmark vazar do círculo.
    <div style={{ position: 'relative', flexShrink: 0, width: `${size}px`, height: `${size}px` }}>
      <div
        className="voice-avatar-ag"
        onClick={onPlayClick ? (e) => { e.stopPropagation(); onPlayClick(); } : undefined}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          // Background sempre presente (gradient da esfera). Fica visível durante
          // o carregamento da imagem e como fallback caso a img falhe.
          background: sphere.gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 600,
          fontSize: `${Math.round(size * 0.38)}px`,
          fontFamily: 'var(--dp-font)',
          textShadow: '0 1px 3px rgba(0,0,0,0.4)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.1), inset 0 -2px 4px rgba(0,0,0,0.2)',
          position: 'relative',
          overflow: 'hidden',
          cursor: onPlayClick ? 'pointer' : 'default',
          transition: 'transform 0.2s',
        }}
      >
        {/* Imagem artística do R2 (lazy-loaded). Cobre o gradient quando carrega. */}
        {bgUrl && imgStatus !== 'error' && (
          <img
            src={bgUrl}
            alt=""
            loading="lazy"
            draggable={false}
            onLoad={() => setImgStatus('loaded')}
            onError={() => setImgStatus('error')}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              opacity: imgStatus === 'loaded' ? 1 : 0,
              transition: 'opacity 0.2s',
              zIndex: 0,
            }}
          />
        )}

        {/* Letter initial — só aparece em fallback (img falhou de carregar). */}
        {showLetter && (
          <span style={{
            opacity: isPreviewing ? 0 : 1,
            transition: 'opacity 0.15s',
            position: 'relative',
            zIndex: 1,
          }}>
            {initial}
          </span>
        )}

        {/* Play/Pause overlay (em cima da imagem). */}
        {onPlayClick && (
          <div className={isPreviewing ? 'play-overlay-ag visible' : 'play-overlay-ag'} style={{
            position: 'absolute',
            top: 0, left: 0, width: '100%', height: '100%',
            borderRadius: '50%',
            background: isPreviewing ? 'rgba(13,174,255,0.85)' : 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isPreviewing ? 1 : 0,
            transition: 'opacity 0.2s',
            zIndex: 2,
          }}>
            {isPreviewing
              ? <Pause size={Math.round(size * 0.38)} style={{ color: 'white' }} />
              : <Play size={Math.round(size * 0.38)} style={{ color: 'white', marginLeft: '2px' }} />
            }
          </div>
        )}
      </div>

      {/* Checkmark de qualidade — estilo "verificado" IG/X (BadgeCheck SVG do
          lucide com fill colorido e check branco). Drop-shadow duplo cria o
          "halo" característico que separa visualmente do avatar de fundo —
          mesmo efeito da borda branca usada por Instagram e Twitter/X. */}
      {showCheck && (
        <div
          style={{
            position: 'absolute',
            top: -3,
            right: -3,
            left: 'auto',
            zIndex: 5,
            pointerEvents: 'auto',
            // Halo claro (cor do fundo do card) — separa o selo da foto do avatar.
            // Um único drop-shadow sutil (~1px) pra borda fina estilo IG/X.
            filter: 'drop-shadow(0 0 0.8px var(--dp-bg-card)) drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
            lineHeight: 0,
          }}
        >
          <DarkTooltip
            content={isProfessional ? 'Clone de Voz Profissional' : 'Clone de Voz Profissional - Qualidade Estúdio'}
            delay={300}
          >
            <span
              onClick={(e) => e.stopPropagation()}
              style={{ display: 'inline-flex', cursor: 'default' }}
            >
              <BadgeCheck
                size={checkSize}
                fill={isProfessional ? '#FFB300' : '#1F2937'}
                color="#FFFFFF"
                strokeWidth={2.5}
              />
            </span>
          </DarkTooltip>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PILL STYLES — botões "MP3", "SRT", "VEO", lixeira na linha de ações do card
// de resultado. Definidos top-level pra não recriar a cada render.
// ═══════════════════════════════════════════════════════════════════════════
const RESULT_PILL_BASE = {
  padding: '3px 8px',
  borderRadius: '6px',
  fontSize: '10px',
  fontWeight: 500,
  fontFamily: 'var(--dp-font)',
  border: '1px solid transparent',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  transition: 'background 0.15s, color 0.15s',
  whiteSpace: 'nowrap',
};
// Pills de resultado — DISCRETOS no azul #0DAEFF (tint sutil). Funciona em
// dark e light (tint azul sobre qualquer fundo). Hover existente intensifica.
const RESULT_PILL_CYAN = {
  ...RESULT_PILL_BASE,
  background: 'rgba(13,174,255,0.06)',
  color: '#0DAEFF',
  border: '1px solid rgba(13,174,255,0.14)',
};
const RESULT_PILL_VEO = {
  ...RESULT_PILL_BASE,
  background: 'rgba(13,174,255,0.06)',
  color: '#0DAEFF',
  border: '1px solid rgba(13,174,255,0.14)',
};
const RESULT_PILL_GHOST = {
  ...RESULT_PILL_BASE,
  background: 'transparent',
  color: 'var(--dp-text-tertiary)',
};

// Código curto provisório p/ o nome de áudio SEM título, usado só enquanto o
// job ainda não tem id do backend (durante o "processando").
const shortAudioCode = () => Math.random().toString(36).slice(2, 8);

// É o nome padrão (gerado por nós), e não um nome que o usuário deu?
const isDefaultAudioName = (name) => !name || /^darkplanner_audio_/i.test(name);

// Código DEFINITIVO do áudio sem título: os 8 primeiros caracteres do job_id
// (UUID que o backend gera por áudio). É único por design, então nunca dois
// downloads colidem/sobrescrevem. Ex.: darkplanner_audio_31f7c42e.
const audioCodeFromId = (id) =>
  `darkplanner_audio_${String(id || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8) || shortAudioCode()}`;

// Casa um item da fila com o registro REAL do /history. Necessário porque um
// timeout/erro no POST /generate pode deixar o item com o id LOCAL aleatório e
// SEM jobId (o áudio existe pronto no servidor, mas o polling nunca casava).
// Ordem: (a) jobId exato, (b) id == job_id, (c) id == row.id, e por fim
// (d) FALLBACK ÓRFÃO por file_name. O nome default carrega um código aleatório
// único (darkplanner_audio_<code>), então file_name sozinho já é chave forte;
// created_at só desempata quando há vários com o mesmo nome — tolerante a fuso,
// porque o /history manda hora de Brasília SEM offset (get_brazil_now).
function matchHistoryItem(queueItem, historyItems, opts = {}) {
  if (!queueItem || !Array.isArray(historyItems) || historyItems.length === 0) return null;
  const claimed = opts.claimedJobIds;
  const exact = historyItems.find(h =>
    (queueItem.jobId && h.job_id === queueItem.jobId) ||
    (queueItem.id && h.job_id === queueItem.id) ||
    (queueItem.id && h.id === queueItem.id)
  );
  if (exact) return exact;
  const fname = queueItem.fileName;
  if (!fname) return null;
  const cands = historyItems.filter(h =>
    h.file_name === fname && (!claimed || !claimed.has(h.job_id))
  );
  if (cands.length <= 1) return cands[0] || null;
  // Vários com o mesmo nome: pega o created_at mais próximo do item da fila.
  const qt = queueItem.createdAt ? new Date(queueItem.createdAt).getTime() : null;
  if (qt === null) return cands[0];
  const distMs = (h) => {
    if (!h.created_at) return Infinity;
    // Sem offset no ISO? trata como -03:00 (Brasília) p/ não depender do fuso do navegador.
    const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(h.created_at) ? h.created_at : `${h.created_at}-03:00`;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? Infinity : Math.abs(t - qt);
  };
  return cands.slice().sort((a, b) => distMs(a) - distMs(b))[0];
}

// Botão "Baixar MP3" — download principal do áudio (azul tint, premium).
// MESMA altura/tipografia do SEG_BTN: todos os botões do card no mesmo padrão.
const DOWNLOAD_BTN = {
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  padding: '4px 9px', borderRadius: '8px',
  fontSize: '10.5px', fontWeight: 600, fontFamily: 'var(--dp-font)',
  background: 'rgba(13,174,255,0.10)', color: '#0DAEFF',
  border: '1px solid rgba(13,174,255,0.22)',
  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
};
// Segmento do grupo de legendas SRT (cada legenda individual, mas juntas).
const SEG_BTN = {
  display: 'inline-flex', alignItems: 'center', gap: '4px',
  padding: '4px 9px', fontSize: '10.5px', fontWeight: 500, fontFamily: 'var(--dp-font)',
  background: 'transparent', color: 'var(--dp-text-secondary)', border: 'none',
  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.15s, color 0.15s',
};

// Estilo PADRÃO do ícone "Excluir" (lixeira). Usado em 3 lugares:
//   - Header da Col 3: "Limpar concluídos" (sem seleção)
//   - Header da Col 3: "Excluir selecionados" (com seleção)
//   - Pill no card de áudio: excluir item individual
// Discreto por padrão (transparent + tertiary), hover vermelho.
const TRASH_ICON_BTN = {
  background: 'transparent',
  border: 'none',
  color: 'var(--dp-text-tertiary)',
  cursor: 'pointer',
  padding: '6px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.15s, color 0.15s',
  fontFamily: 'var(--dp-font)',
};

// ═══════════════════════════════════════════════════════════════════════════
// WAVEFORM COMPONENT — visual bars for result cards
// ═══════════════════════════════════════════════════════════════════════════
const Waveform = ({ bars = 45, activeRatio = 0, height = 28 }) => {
  const barsRef = useRef(null);

  useEffect(() => {
    if (!barsRef.current) return;
    const container = barsRef.current;
    container.innerHTML = '';
    for (let i = 0; i < bars; i++) {
      const h = Math.random() * (height - 3) + 3;
      const bar = document.createElement('div');
      bar.style.cssText = `width:2.5px;height:${h}px;border-radius:2px;background:var(--dp-blue-500);opacity:${i < bars * activeRatio ? '0.7' : '0.15'};transition:opacity 0.15s;flex-shrink:0;`;
      container.appendChild(bar);
    }
  }, [bars, activeRatio, height]);

  return (
    <div ref={barsRef} style={{
      display: 'flex',
      alignItems: 'center',
      gap: '1.5px',
      minWidth: '100px',
      height: `${height}px`,
      flex: '0.8',
    }} />
  );
};


function AudioGeneratorPageInner({ onLogout }) {
  const { theme, isDarkMode } = useTheme();
  const { isCollapsed, sidebarWidth, isMobile } = useSidebar();
  const { t } = useTranslation();

  // ═══════════════════════════════════════════════════════════════════════
  // STATE — All original state preserved
  // ═══════════════════════════════════════════════════════════════════════
  const [text, setText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [speed, setSpeed] = useState(1.0);
  const [volume, setVolume] = useState(1.0);
  const [subtitleWords, setSubtitleWords] = useState(5);       // legenda.srt: máx. palavras/linha (pausas naturais)
  const [subtitleCase, setSubtitleCase] = useState('normal');
  const [subtitleVeoWords, setSubtitleVeoWords] = useState(5); // srtsyncpalavra.srt: total FIXO de palavras/linha
  // srtsynctempo.srt: range de duração ALEATÓRIA dos blocos (1-60s). Padrão 3-8 —
  // o DarkPlanner SEMPRE envia (nunca cai no 8s fixo do Carlos). max sempre >= min.
  const [subtitleVeoMin, setSubtitleVeoMin] = useState(7);
  const [subtitleVeoMax, setSubtitleVeoMax] = useState(8);
  const [fileName, setFileName] = useState('');

  // limit/remaining começam null: evita exibir o DEFAULT antigo (20) antes do
  // /usage carregar (free = 5, pagante = limite diário dele — vêm do backend).
  const [usage, setUsage] = useState({ used: 0, limit: null, remaining: null, can_generate: true, unlimited: false, plan_type: 'default', character_limit: 80000, is_free_lifetime: false });

  const [queue, setQueue] = useState([]);
  const [processingId, setProcessingId] = useState(null);

  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const [showSettingsPopover, setShowSettingsPopover] = useState(false);
  const [rightTab, setRightTab] = useState('config'); // 'config' | 'history' — aba do painel direito
  // Layout: 3 colunas (padrão) OU 2 colunas com o painel (config/resultados) embaixo,
  // ocupando todo o container. Só uma opção visual; persiste a preferência.
  const [wideResults, setWideResults] = useState(() => {
    try { return localStorage.getItem('ag_layout') === '2col'; } catch { return false; }
  });
  const toggleLayout = () => setWideResults((v) => {
    const next = !v;
    try { localStorage.setItem('ag_layout', next ? '2col' : '3col'); } catch { /* ignore */ }
    return next;
  });
  // No layout 2-col (painel embaixo) a config fica COLAPSADA atrás de um botão;
  // só abre quando o usuário clica. Mantém a página enxuta (resultados em foco).
  const [cfgOpen, setCfgOpen] = useState(false);
  const [useCache, setUseCache] = useState(true);
  const [includeSrt, setIncludeSrt] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  const [playingItemId, setPlayingItemId] = useState(null);
  const [playerCurrentTime, setPlayerCurrentTime] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(0);

  const [favorites, setFavorites] = useState([]);
  const [voiceFilter, setVoiceFilter] = useState('all');
  // Filtro de idioma da lista lateral ('all' = todos; senão código ISO ex. 'pt')
  const [voiceLangFilter, setVoiceLangFilter] = useState('all');
  // Variações de voz do usuário (voz do catálogo + nome customizado — "Minhas Vozes")
  const [variations, setVariations] = useState([]);
  // Modal de variação: { mode: 'create', voice } | { mode: 'edit', variation } | null
  const [variationModal, setVariationModal] = useState(null);

  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [zipProgress, setZipProgress] = useState({ current: 0, total: 0 });

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Modal de texto do áudio
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [textModalItem, setTextModalItem] = useState(null);

  // Editor de SRT Tempo
  const [srtEditorOpen, setSrtEditorOpen] = useState(false);
  const [srtEditorItem, setSrtEditorItem] = useState(null);

  const [canais, setCanais] = useState([]);
  const [selectedCanal, setSelectedCanal] = useState('');
  const [roteirosDisponiveis, setRoteirosDisponiveis] = useState([]);
  const [selectedRoteiro, setSelectedRoteiro] = useState('');

  // ─── MULTIVOZ ─────────────────────────────────────────────────────────────
  // ID real da voz "Multivoz" (descoberto dinamicamente em fetchVoices, NÃO hardcoded)
  const [multivoiceVoiceId, setMultivoiceVoiceId] = useState(null);
  const [isMultivoiceMode, setIsMultivoiceMode] = useState(false);
  // Texto debounced (300ms) pra detector de vozes em tempo real (visual/informativo)
  const [debouncedText, setDebouncedText] = useState('');
  // Modal de troca de voz (substitui [slug_antigo] por [slug_novo] no textarea)
  const [swapVoiceOpen, setSwapVoiceOpen] = useState(false);
  const [swapTargetSlug, setSwapTargetSlug] = useState(null);
  const [swapSearch, setSwapSearch] = useState('');
  const [swapFilter, setSwapFilter] = useState('all'); // all | favorites | female | male

  // ─── FASE 2: Biblioteca de Vozes (modal expandido com dropdown inline) ──
  const [libraryModalOpen, setLibraryModalOpen] = useState(false);
  // FASE 2.1: idioma selecionado no dropdown inline do header da voz central
  const [selectedLanguageKey, setSelectedLanguageKey] = useState(null);
  // Popover de "+N idiomas" no header da voz selecionada (Coluna 2)
  const [headerLangAnchor, setHeaderLangAnchor] = useState(null);

  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewAudioRef = useRef(null);
  const settingsBtnRef = useRef(null);
  const settingsPopupRef = useRef(null);
  const durationFetchedRef = useRef(new Set());
  const textareaRef = useRef(null);
  const [pauseMenuOpen, setPauseMenuOpen] = useState(false);

  // Insere a tag SSML de pausa <break time="Xs"/> na posição do cursor do textarea.
  // O TTS preserva essa tag (a limpeza do backend não a remove). Só mexe no texto/cursor.
  const insertBreakTag = (seconds) => {
    const tag = `<break time="${seconds}s"/>`;
    const el = textareaRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const before = text.slice(0, start);
    const after = text.slice(end);
    const sep = before && !/\s$/.test(before) ? ' ' : '';
    const insert = `${sep}${tag} `;
    setText(before + insert + after);
    const newPos = (before + insert).length;
    requestAnimationFrame(() => {
      if (el) { el.focus(); try { el.setSelectionRange(newPos, newPos); } catch {} }
    });
    setPauseMenuOpen(false);
  };

  const [previewingVoiceId, setPreviewingVoiceId] = useState(null);

  // Audio preferences hierarchy state
  const [configSource, setConfigSource] = useState(null);     // 'system'|'user'|'channel'
  const [configChannelName, setConfigChannelName] = useState('');
  const [savingPrefs, setSavingPrefs] = useState(false);

  // ═══════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Click na progress bar de um card de resultado.
  // - Se o item já está tocando: faz seek (ajusta currentTime do audioRef).
  // - Caso contrário: dispara play (começa do 0).
  // Só aceita clicks em items completed + com audioUrl (caller já filtra).
  const handleSeekOrPlay = (e, currentItem) => {
    if (playingItemId === currentItem.id && audioRef.current && playerDuration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      audioRef.current.currentTime = percent * playerDuration;
      return;
    }
    togglePlayItem(currentItem);
  };

  // Data relativa pra cards de resultado: "hoje 14:32", "ontem 14:32",
  // "qua 14:32" (mesma semana) ou "12/05 14:32" (mais antigo).
  const formatRelativeDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const sameYMD = (a, b) =>
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameYMD(date, now)) return `hoje ${time}`;
    if (sameYMD(date, yesterday)) return `ontem ${time}`;
    const diffDays = (now - date) / (1000 * 60 * 60 * 24);
    if (diffDays < 7 && diffDays > 0) {
      const weekdays = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
      return `${weekdays[date.getDay()]} ${time}`;
    }
    return `${date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${time}`;
  };

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const narrationNormal = (() => { const s = Math.round((wordCount / 155) * 60); return `${Math.floor(s / 60)}m ${s % 60}s`; })();
  const narrationSlow = (() => { const s = Math.round((wordCount / 130) * 60); return `${Math.floor(s / 60)}m ${s % 60}s`; })();
  const narrationFast = (() => { const s = Math.round((wordCount / 183) * 60); return `${Math.floor(s / 60)}m ${s % 60}s`; })();

  // ─── MULTIVOZ: sincroniza isMultivoiceMode com a voz selecionada ─────────
  // Substitui o handleVoiceSelect anterior. Quando user clica numa voz na lista
  // (onClick = setSelectedVoice), este efeito decide se modo multivoz ativa.
  useEffect(() => {
    if (!selectedVoice) {
      setIsMultivoiceMode(false);
      return;
    }
    const isMv =
      (multivoiceVoiceId && selectedVoice === multivoiceVoiceId) ||
      voices.find((v) => v.id === selectedVoice)?.name === 'Multivoz';
    setIsMultivoiceMode(!!isMv);
  }, [selectedVoice, multivoiceVoiceId, voices]);

  // ─── MULTIVOZ: debounce do texto (300ms) pro detector de vozes em tempo real ─
  useEffect(() => {
    const tid = setTimeout(() => setDebouncedText(text), 300);
    return () => clearTimeout(tid);
  }, [text]);

  // ─── MULTIVOZ: vozes detectadas a partir dos slugs no texto debounced ────
  // Cada item: { slug, voice (objeto da biblioteca ou undefined), valid (bool) }.
  const detectedVoicesInfo = useMemo(() => {
    if (!isMultivoiceMode) return [];
    const slugs = [...new Set(
      debouncedText.match(/\[([a-z0-9_]+)\]/gi)?.map((s) => s.slice(1, -1).toLowerCase()) || []
    )];
    return slugs.map((slug) => {
      const voice = voices.find((v) => (v.slug || '').toLowerCase() === slug);
      return { slug, voice, valid: !!voice };
    });
  }, [isMultivoiceMode, debouncedText, voices]);

  // ─── MULTIVOZ: lista filtrada do modal de troca (exclui Multivoz, aplica search) ─
  const swapVoiceList = useMemo(() => {
    let list = voices.filter((v) => !multivoiceVoiceId || v.id !== multivoiceVoiceId);
    if (swapFilter === 'favorites') {
      list = list.filter((v) => favorites.includes(v.id));
    } else if (swapFilter === 'female') {
      list = list.filter((v) => v.gender === 'female');
    } else if (swapFilter === 'male') {
      list = list.filter((v) => v.gender === 'male');
    }
    if (swapSearch.trim()) {
      // Mesma busca rica da lista lateral (multi-token, sem acento, nome +
      // slug + idiomas + categoria/uso traduzidos)
      list = list.filter((v) => matchesSearch(v, swapSearch));
    }
    return list;
  }, [voices, multivoiceVoiceId, swapSearch, swapFilter, favorites]);

  // ─── MULTIVOZ: helper que renderiza o "miolo" visual de um item de voz ─────
  // Reutilizado pela biblioteca lateral E pelo modal de trocar voz.
  // Retorna o JSX do avatar + bloco texto. Botões absolutos (favorito, copy slug)
  // ficam INLINE no caller (não fazem parte do helper).
  // showSlug=true exibe "SLUG: [voice_slug]" no início da linha 2 (modo modal).
  const renderVoiceVisual = (voice, { isSelected = false, isPreviewing = false, onPlayClick, showSlug = false } = {}) => (
    <>
      <VoiceAvatar
        voice={voice}
        size={30}
        isPreviewing={isPreviewing}
        onPlayClick={onPlayClick}
      />
      <div style={{ flex: 1, minWidth: 0 }} title={voice.name}>
        <div style={{
          fontSize: '12px',
          fontWeight: isSelected ? 500 : 400,
          color: isSelected ? 'var(--dp-blue-500)' : 'var(--dp-text-primary)',
          lineHeight: '1.3',
          marginBottom: '1px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{voice.name}</span>
          {voice.category === 'professional' && (
            <span style={{
              width: '13px', height: '13px', borderRadius: '50%',
              background: 'var(--dp-blue-500)', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: '7px', color: 'white', flexShrink: 0,
            }}>✓</span>
          )}
          {voice.name === 'Multivoz' && (
            <span style={{
              fontSize: '8.5px',
              fontWeight: 700,
              color: 'var(--dp-blue-500)',
              background: 'rgba(13,174,255,0.14)',
              border: '1px solid rgba(13,174,255,0.35)',
              padding: '1px 5px',
              borderRadius: '9999px',
              letterSpacing: '0.4px',
              flexShrink: 0,
              lineHeight: 1.2,
            }}>MULTIVOZ</span>
          )}
        </div>
        <div style={{
          fontSize: '10px',
          color: '#9CA3AF',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {showSlug && voice.slug && (
            <>
              <span style={{ color: 'var(--dp-blue-500)', opacity: 0.85, fontFamily: 'monospace' }}>SLUG: [{voice.slug}]</span>
              <span> · </span>
            </>
          )}
          {voice.genero || (voice.gender === 'female' ? 'Feminino' : voice.gender === 'male' ? 'Masculino' : voice.gender || '')}
          {voice.provider && <> · <span style={{ color: 'var(--dp-blue-500)', opacity: 0.7 }}>{voice.provider}</span></>}
        </div>
      </div>
    </>
  );

  const openSwap = useCallback((slug) => {
    setSwapTargetSlug(slug);
    setSwapSearch('');
    setSwapFilter('all');
    setSwapVoiceOpen(true);
  }, []);

  // Substitui TODAS as ocorrências de [oldSlug] por [newSlug] no textarea.
  // Case-insensitive no match (regex /gi); preserva o resto do texto intacto.
  const performSwap = useCallback((oldSlug, newSlug) => {
    if (!oldSlug || !newSlug || oldSlug === newSlug) {
      setSwapVoiceOpen(false);
      setSwapTargetSlug(null);
      return;
    }
    const escaped = oldSlug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\[${escaped}\\]`, 'gi');
    setText((prev) => prev.replace(regex, `[${newSlug}]`));
    setSwapVoiceOpen(false);
    setSwapTargetSlug(null);
    toast.success(`[${oldSlug}] → [${newSlug}]`);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // DATA FETCHING — All original logic preserved
  // ═══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    fetchVoices();
    fetchUsage();
    fetchHistory();
    fetchCanais();
    fetchRoteirosDisponiveis(null);
    resolveAudioConfig(null);  // Load user preferences on mount
  }, []);

  useEffect(() => {
    if (selectedCanal !== undefined) {
      fetchRoteirosDisponiveis(selectedCanal || null);
      setSelectedRoteiro('');
      resolveAudioConfig(selectedCanal || null);  // Re-resolve config on channel change
    }
  }, [selectedCanal]);

  // Polling for processing items
  const pollingRef = useRef(null);
  // queue SEMPRE fresco dentro do setInterval: a closure do effect congela o
  // queue do render em que rodou; o ref evita ler estado velho no polling.
  const queueRef = useRef(queue);
  queueRef.current = queue;
  const hasProcessing = queue.some(q => q.status === 'processing');

  useEffect(() => {
    const processingItems = queue.filter(q => q.status === 'processing');

    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    if (processingItems.length === 0) return;

    pollingRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const processingCount = queueRef.current.filter(q => q.status === 'processing').length;
        const historyLimit = Math.max(50, processingCount + 20);
        const response = await axios.get(`${BACKEND_URL}/api/audio-tts/history?limit=${historyLimit}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const historyItems = response.data.history || [];

        setQueue(prev => {
          let hasChanges = false;
          // job_ids já reivindicados por itens com jobId, p/ dois órfãos não
          // casarem com o MESMO registro no fallback por file_name.
          const claimed = new Set(prev.filter(q => q.jobId).map(q => q.jobId));
          const updated = prev.map(queueItem => {
            if (queueItem.status !== 'processing') return queueItem;

            const historyItem = matchHistoryItem(queueItem, historyItems, { claimedJobIds: claimed });

            if (!historyItem) return queueItem;

            if (historyItem.status === 'completed') {
              hasChanges = true;
              const wasRegenerating = queueItem.isRegenerating || queueItem.backendStatus === 'failed';
              toast.success(`"${queueItem.fileName}" ${wasRegenerating ? t('audio.regenerated') : t('audio.done')}!`);

              if (queueItem.fromRoteiro) {
                marcarAudioGerado(queueItem.fromRoteiro);
              }

              return {
                ...queueItem,
                status: 'completed',
                backendStatus: 'completed',
                jobId: historyItem.job_id,
                audioUrl: historyItem.audio_url,
                srt: historyItem.srt_url,
                srtPalavra: historyItem.srt_palavra_url || null,
                srtTempo: historyItem.srt_tempo_url || historyItem.srt_veo_url || null,
                srtVeo: historyItem.srt_veo_url || null,
                duration: historyItem.duration || null,
                available: true,
                isRegenerating: false,
                expiresInHours: historyItem.expires_in_hours || null
              };
            }

            if (historyItem.status === 'blocked') {
              // Moderação da voz barrou o texto: erro DEFINITIVO (não fica
              // "Gerando" — o retry externo não resolve; o roteiro precisa
              // ser revisado). Diferente do 'failed' genérico, que o backend
              // trata como transitório (segue processing até completar).
              hasChanges = true;
              toast.error(t('audio.moderation_blocked_toast', { name: queueItem.fileName }));
              return {
                ...queueItem,
                status: 'error',
                backendStatus: 'blocked',
                errorMessage: historyItem.error_message || t('audio.moderation_blocked'),
                isRegenerating: false,
              };
            }

            if (historyItem.status === 'failed') {
              if (!queueItem.isRegenerating && queueItem.backendStatus !== 'failed') {
                hasChanges = true;
                return { ...queueItem, backendStatus: 'failed', isRegenerating: true };
              }
            }

            // Casou (provável pelo fallback órfão) mas ainda processando: grava o
            // jobId/id REAIS p/ convergir e não depender do fallback nos próximos polls.
            if (historyItem.job_id && queueItem.jobId !== historyItem.job_id) {
              hasChanges = true;
              return {
                ...queueItem,
                jobId: historyItem.job_id,
                id: queueItem.jobId ? queueItem.id : historyItem.job_id,
              };
            }

            return queueItem;
          });

          if (hasChanges) fetchUsage();
          return hasChanges ? updated : prev;
        });
        // Always refresh usage during active processing to keep counter accurate
        fetchUsage();
      } catch (error) {
        console.error('Erro no polling:', error);
      }
    }, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [hasProcessing]);

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('token');
      // ETAPA 3 — manda o device fingerprint p/ o /usage devolver o remaining REAL
      // (conta + device + IP). Await garante que o badge não pisque "0/5" antes do fp.
      const deviceFp = await getDeviceFp();
      const response = await axios.get(`${BACKEND_URL}/api/audio-tts/usage`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Device-FP': deviceFp || '' }
      });
      const data = response.data;
      setUsage({
        used: data.used_today ?? 0,
        limit: data.limit,
        remaining: data.remaining ?? 0,
        can_generate: data.can_generate !== false,
        unlimited: data.unlimited ?? false,
        plan_type: data.plan_type ?? 'unknown',
        character_limit: data.character_limit,
        is_free_lifetime: data.is_free_lifetime === true
      });
    } catch (error) {
      console.error('Erro ao buscar uso:', error);
    }
  };

  // ETAPA 3 — conta free que já nasce esgotada (mesmo device/IP que já gastou os 5):
  // mostra a mensagem de assinar JÁ ao carregar, antes de escrever/clicar. Dispara
  // uma vez por carga; pagante (is_free_lifetime=false) nunca entra aqui.
  const freeBlockNotifiedRef = useRef(false);
  useEffect(() => {
    if (usage.is_free_lifetime && usage.can_generate === false && !freeBlockNotifiedRef.current) {
      freeBlockNotifiedRef.current = true;
      toast.error(t('audio.free_limit_short'));
      try { window.dispatchEvent(new CustomEvent('premium-required', { detail: { feature: 'audio_free' } })); } catch {}
    }
  }, [usage.is_free_lifetime, usage.can_generate]);

  const fetchHistory = async (retryLeft = 2) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/audio-tts/history?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

      const historyItems = (response.data.history || []).map(item => {
        let frontendStatus = item.status || 'completed';
        const backendStatus = item.status;
        if (frontendStatus === 'failed') frontendStatus = 'processing';
        // 'blocked' = moderação da voz barrou o TEXTO (definitivo, retry não
        // resolve) → vira card de erro com a mensagem de revisar o roteiro.
        if (backendStatus === 'blocked') frontendStatus = 'error';

        return {
          id: item.job_id || item.id,
          text: item.text_preview,
          // Sem full_text na lista do /history (pesava no servidor): o modal
          // busca o roteiro completo sob demanda (openTextModal).
          fullText: null,
          textLength: item.text_length || 0,
          voiceId: item.voice_id,
          voiceName: item.voice_name || item.voice_id,
          fileName: isDefaultAudioName(item.file_name) ? audioCodeFromId(item.job_id || item.id) : item.file_name,
          status: frontendStatus,
          backendStatus: backendStatus,
          // Bloqueado: a mensagem do SERVIDOR exibe como vem (contrato do audio
          // server); i18n só para os rótulos. Fallback se a coluna estiver vazia.
          errorMessage: backendStatus === 'blocked' ? (item.error_message || t('audio.moderation_blocked')) : undefined,
          audioUrl: item.audio_url || null,
          jobId: item.job_id,
          createdAt: new Date(item.created_at),
          fromHistory: true,
          available: frontendStatus === 'completed',
          srt: item.srt_url || null,
          srtPalavra: item.srt_palavra_url || null,
          srtTempo: item.srt_tempo_url || item.srt_veo_url || null,
          srtVeo: item.srt_veo_url || null,
          duration: item.duration || null,
          isRegenerating: backendStatus === 'failed',
          expiresInHours: item.expires_in_hours || null
        };
      });

      const filteredItems = historyItems.filter(item => {
        if (item.status === 'completed') return true;
        if (item.status === 'expired') return true;
        if (item.status === 'processing' && item.createdAt > twentyFourHoursAgo) return true;
        // Bloqueado por moderação: visível por 24h (mesma janela do processing)
        // pra o usuário entender por que o áudio não saiu.
        if (item.backendStatus === 'blocked' && item.createdAt > twentyFourHoursAgo) return true;
        return false;
      });

      filteredItems.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });

      setQueue(filteredItems);
    } catch (error) {
      // Falha aqui NÃO pode virar fila vazia silenciosa: o /history é pesado
      // (self-heal inline) e um timeout no F5 fazia o áudio recém-gerado
      // "sumir" sem aviso. Retry com backoff; persistindo, avisa o usuário.
      console.error('Erro ao buscar histórico:', error);
      if (retryLeft > 0) {
        setTimeout(() => fetchHistory(retryLeft - 1), 4000);
      } else {
        toast.error(t('audio.history_load_error', 'Não foi possível carregar seu histórico de áudios. Recarregue a página.'));
      }
    }
  };

  // Abre o modal do roteiro. Itens do histórico chegam só com a prévia (200
  // chars): o texto completo vem de /history/{job_id}/text na 1ª abertura e
  // fica guardado no item da fila.
  const openTextModal = async (item) => {
    setTextModalItem(item);
    setTextModalOpen(true);
    if (item.fullText || !item.fromHistory || !item.jobId) return;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${BACKEND_URL}/api/audio-tts/history/${item.jobId}/text`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullText = data.full_text || item.text || '';
      setTextModalItem(prev => (prev && prev.id === item.id ? { ...prev, fullText } : prev));
      setQueue(prev => prev.map(q => (q.id === item.id ? { ...q, fullText } : q)));
    } catch (error) {
      console.error('Erro ao buscar o texto do áudio:', error);
    }
  };

  // Recupera o jobId REAL de um item que perdeu o jobId num timeout/erro do POST
  // /generate (o áudio costuma existir pronto no servidor). NÃO usa fetchHistory
  // aqui de propósito: fetchHistory faz setQueue(filteredItems) e REESCREVE a fila
  // inteira (apagaria pendentes e teria race). Aqui é cirúrgico — casa pelo
  // /history e grava SÓ naquele item. Se ainda não casar (linha não commitada),
  // fica processing e o polling (matchHistoryItem) resolve no próximo ciclo.
  const resolveOrphanFromHistory = async (orphanItem) => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${BACKEND_URL}/api/audio-tts/history?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const items = resp.data.history || [];
      const match = matchHistoryItem(orphanItem, items);
      if (!match || !match.job_id) return;
      setQueue(prev => prev.map(q => (q.id === orphanItem.id ? {
        ...q,
        jobId: match.job_id,
        id: match.job_id,
        fileName: isDefaultAudioName(q.fileName) ? audioCodeFromId(match.job_id) : q.fileName,
      } : q)));
    } catch (e) {
      console.error('resolveOrphanFromHistory falhou:', e);
    }
  };

  const fetchCanais = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/canais`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ownCanais = response.data || [];

      // Buscar canais compartilhados aceitos
      let sharedCanais = [];
      try {
        const sharedResponse = await axios.get(`${BACKEND_URL}/api/canais/compartilhados`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        sharedCanais = (sharedResponse.data || [])
          .filter(c => c.comp_status === 'aceito')
          .map(c => ({ ...c, _is_shared: true }));
      } catch (e) {
        // Silently fail — shared channels are optional
      }

      setCanais([...ownCanais, ...sharedCanais]);
    } catch (error) {
      console.error('Erro ao buscar canais:', error);
    }
  };

  // ── Resolve audio config from hierarchy: channel > user > system ──
  const resolveAudioConfig = async (canalId = null) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${BACKEND_URL}/api/audio-config/resolve`;
      if (canalId) url += `?canal_id=${canalId}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const { config, source, channel_name } = res.data;
      if (config.voice_id) setSelectedVoice(config.voice_id);
      if (config.speed != null) setSpeed(config.speed);
      if (config.volume != null) setVolume(config.volume);
      if (config.subtitle_enabled != null) setIncludeSrt(config.subtitle_enabled);
      if (config.subtitle_words != null) setSubtitleWords(config.subtitle_words);
      if (config.subtitle_case) setSubtitleCase(config.subtitle_case);
      if (config.subtitle_veo_words != null) setSubtitleVeoWords(config.subtitle_veo_words);
      if (config.subtitle_veo_min != null) setSubtitleVeoMin(config.subtitle_veo_min);
      if (config.subtitle_veo_max != null) setSubtitleVeoMax(config.subtitle_veo_max);
      setConfigSource(source);
      setConfigChannelName(channel_name || '');
    } catch {
      // Silently fail — keeps current state (existing behavior preserved)
    }
  };

  // ── Save current settings as user defaults ──
  const saveAsDefault = async () => {
    setSavingPrefs(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/audio-preferences`, {
        voice_id: selectedVoice || null,
        voice_name: voices.find(v => v.id === selectedVoice)?.name || null,
        speed,
        volume,
        subtitle_enabled: includeSrt,
        subtitle_words: subtitleWords,
        subtitle_case: subtitleCase,
        subtitle_veo_words: subtitleVeoWords,
        subtitle_veo_min: subtitleVeoMin,
        subtitle_veo_max: subtitleVeoMax,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Preferências salvas como padrão');
      setConfigSource('user');
    } catch {
      toast.error('Erro ao salvar preferências');
    } finally {
      setSavingPrefs(false);
    }
  };

  const fetchRoteirosDisponiveis = async (canalId = null) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${BACKEND_URL}/api/roteiros/disponiveis-para-audio`;
      if (canalId) url += `?canal_id=${canalId}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoteirosDisponiveis(response.data.videos || []);
    } catch (error) {
      console.error('Erro ao buscar roteiros:', error);
      setRoteirosDisponiveis([]);
    }
  };

  const marcarAudioGerado = async (videoId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/videos/${videoId}/marcar-audio-gerado`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRoteirosDisponiveis(selectedCanal || null);
    } catch (error) {
      console.error('Erro ao marcar áudio como gerado:', error);
    }
  };

  const handleSelecionarRoteiro = (roteiroId) => {
    setSelectedRoteiro(roteiroId);
    const roteiro = roteirosDisponiveis.find(r => r.video_id === roteiroId);
    if (roteiro && roteiro.roteiro_conteudo) {
      setText(roteiro.roteiro_conteudo);
      setFileName(roteiro.titulo || `Vídeo_${roteiro.numero_video || 'SN'}`);
      toast.success(t('audio.script_loaded'));
      // Auto-resolve audio config from the roteiro's channel (narrator > user > system)
      if (roteiro.canal_id) {
        resolveAudioConfig(roteiro.canal_id);
      }
    }
  };

  // Audio progress
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleTimeUpdate = () => setProgress((audio.currentTime / audio.duration) * 100 || 0);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => { setIsPlaying(false); setProgress(0); };
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentAudio]);

  // Proactively fetch duration for completed items missing it (client-side fallback)
  useEffect(() => {
    const items = queue.filter(
      q => q.status === 'completed' && !q.duration && q.audioUrl && q.audioUrl.startsWith('http') && !durationFetchedRef.current.has(q.id)
    );
    if (items.length === 0) return;

    items.forEach(item => {
      durationFetchedRef.current.add(item.id);
      const audio = new Audio();
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => {
        const dur = audio.duration;
        if (dur && isFinite(dur)) {
          setQueue(prev => prev.map(q =>
            q.id === item.id && !q.duration ? { ...q, duration: dur } : q
          ));
        }
        audio.src = '';
        audio.onloadedmetadata = null;
        audio.onerror = null;
      };
      audio.onerror = () => {
        audio.src = '';
        audio.onloadedmetadata = null;
        audio.onerror = null;
      };
      audio.src = item.audioUrl;
    });
  }, [queue]);

  // Load favorites from API (fallback: localStorage)
  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BACKEND_URL}/api/audio-tts/favorites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const ids = (res.data.favorites || []).map(f => f.voice_id);
        setFavorites(ids);
        localStorage.setItem('audio_voice_favorites', JSON.stringify(ids));
      } catch {
        // Fallback: localStorage
        const saved = localStorage.getItem('audio_voice_favorites');
        if (saved) { try { setFavorites(JSON.parse(saved)); } catch {} }
      }
    };
    fetchFavorites();
  }, []);

  // Load voice variations (Minhas Vozes com nome customizado)
  useEffect(() => {
    const fetchVariations = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BACKEND_URL}/api/audio-tts/voice-variations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVariations(res.data.variations || []);
      } catch {
        // silencioso — lista de variações fica vazia
      }
    };
    fetchVariations();
  }, []);

  // ─── VARIAÇÕES DE VOZ ─────────────────────────────────────────────────────
  // Salva (create ou edit, conforme variationModal.mode). Chamado pelo modal.
  const saveVariation = async (name) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (variationModal?.mode === 'edit') {
        const res = await axios.put(
          `${BACKEND_URL}/api/audio-tts/voice-variations/${variationModal.variation.id}`,
          { custom_name: name },
          { headers }
        );
        setVariations((prev) => prev.map((v) => (v.id === res.data.variation.id ? res.data.variation : v)));
        toast.success(t('audio.variation_updated'));
      } else {
        const voice = variationModal?.voice;
        if (!voice) return;
        const res = await axios.post(
          `${BACKEND_URL}/api/audio-tts/voice-variations`,
          { voice_id: voice.id, custom_name: name, original_name: voice.name || null },
          { headers }
        );
        setVariations((prev) => [res.data.variation, ...prev]);
        // Ao salvar um nome, a voz base vira favorita automaticamente
        // (fica junto em Minhas Vozes, com o coração preenchido)
        if (!favorites.includes(voice.id)) {
          const newFavorites = [...favorites, voice.id];
          setFavorites(newFavorites);
          try { localStorage.setItem('audio_voice_favorites', JSON.stringify(newFavorites)); } catch { /* ignore */ }
          axios.post(`${BACKEND_URL}/api/audio-tts/favorites`, {
            voice_id: voice.id, voice_name: voice.name || null
          }, { headers }).catch(() => {});
        }
        toast.success(t('audio.variation_created'));
      }
      setVariationModal(null);
    } catch {
      toast.error(t('audio.variation_error'));
    }
  };

  // Exclui variação (optimistic; reverte em falha). Recebe a pseudo-voz da lista.
  const deleteVariation = async (variationVoice) => {
    const variationId = variationVoice.variation_id;
    const prev = variations;
    setVariations((p) => p.filter((v) => v.id !== variationId));
    toast.success(t('audio.variation_deleted'));
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/audio-tts/voice-variations/${variationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {
      setVariations(prev);
    }
  };

  const toggleFavorite = async (voice) => {
    const isFavorite = favorites.includes(voice.id);
    // Optimistic update
    const newFavorites = isFavorite
      ? favorites.filter(id => id !== voice.id)
      : [...favorites, voice.id];
    setFavorites(newFavorites);
    toast.success(isFavorite ? t('audio.removed_favorite') : t('audio.added_favorite'));
    try {
      const token = localStorage.getItem('token');
      if (isFavorite) {
        await axios.delete(`${BACKEND_URL}/api/audio-tts/favorites/${voice.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${BACKEND_URL}/api/audio-tts/favorites`, {
          voice_id: voice.id, voice_name: voice.name || null
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      localStorage.setItem('audio_voice_favorites', JSON.stringify(newFavorites));
    } catch {
      // Revert on failure, keep localStorage as fallback
      setFavorites(favorites);
      localStorage.setItem('audio_voice_favorites', JSON.stringify(favorites));
    }
  };

  const vp = useVoicePreview();

  // Sincroniza os indicadores dos cards (avatar / idioma) com o player
  // COMPARTILHADO. Os reads de previewingVoiceId/selectedLanguageKey continuam
  // iguais nos cards; quem os alimenta agora é o player único da barra inferior.
  useEffect(() => {
    const vid = vp.voice ? vp.voice.id : null;
    setPreviewingVoiceId(vp.isPlaying && vid ? vid : null);
    setSelectedLanguageKey(
      vid === selectedVoice && vp.langKey && vp.langKey !== '__main__' ? vp.langKey : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vp.voice, vp.isPlaying, vp.langKey, selectedVoice]);

  // Voice preview — toca via player compartilhado (barra inferior estilo ElevenLabs);
  // antes pausa um áudio de RESULTADO que esteja tocando.
  const toggleVoicePreview = (voice) => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      setPlayingItemId(null);
    }
    vp.play(voice);
  };

  // Dropdown inline do header central: toca o preview de um idioma específico
  // (mesmo player compartilhado).
  const handleSelectedLanguagePreview = (language, accent, previewUrl, key) => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      setPlayingItemId(null);
    }
    vp.play(selectedVoiceData, { language, accent, previewUrl, key });
  };

  // Quando muda a voz selecionada: para o preview atual.
  useEffect(() => {
    vp.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVoice]);

  const fetchVoices = async () => {
    setLoadingVoices(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/audio-tts/voices`);
      if (response.data.voices) {
        // Previews chegam como caminhos relativos do proxy (catálogo
        // sanitizado, sem voice_id ElevenLabs) — prefixa o backend aqui.
        setVoices(absolutizeVoicePreviews(response.data.voices, BACKEND_URL));
        // Multivoz: descoberta dinâmica do voice_id real (R1; nunca hardcoded)
        const mv = response.data.voices.find((v) => v.name === 'Multivoz');
        if (mv && mv.id) setMultivoiceVoiceId(mv.id);
      }
    } catch (error) {
      console.error('Erro ao carregar vozes:', error);
      toast.error(t('audio.error_load_voices'));
    } finally {
      setLoadingVoices(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // QUEUE OPERATIONS — All original logic preserved
  // ═══════════════════════════════════════════════════════════════════════
  const processQueueItem = async (item) => {
    if (!usage.can_generate) {
      // FASE 4: Free = 5 vitalícios esgotados (não diário); pagante = limite diário.
      const limitMsg = usage.is_free_lifetime
        ? t('audio.free_limit_reached')
        : t('audio.daily_limit_reached', { used: usage.used, limit: usage.limit });
      toast.error(limitMsg);
      if (usage.is_free_lifetime) {
        try { window.dispatchEvent(new CustomEvent('premium-required', { detail: { feature: 'audio_free' } })); } catch {}
      }
      setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'error', errorMessage: limitMsg } : q));
      return;
    }

    setProcessingId(item.id);
    setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'processing' } : q));

    try {
      const token = localStorage.getItem('token');
      const deviceFp = await getDeviceFp();  // ETAPA 3: antifraude do free (pagante ignora)
      const response = await axios.post(`${BACKEND_URL}/api/audio-tts/generate`, {
        text: item.text,
        voice_id: item.voiceId,
        title: item.fileName,
        async_mode: true,
        subtitle_words: subtitleWords,
        subtitle_case: subtitleCase,
        subtitle_veo_words: subtitleVeoWords,
        subtitle_veo_min: subtitleVeoMin,
        subtitle_veo_max: subtitleVeoMax,
        device_fp: deviceFp || undefined
      }, {
        // SEM timeout de propósito: o servidor TTS deixa o áudio pronto e o
        // /history faz self-heal. Abortar por tempo só criava item órfão; esperar
        // a resposta devolve o job_id real (regra do dono: geração enviada nunca falha).
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.data.success) throw new Error(response.data.message || response.data.error || t('audio.audio_load_error'));

      const jobId = response.data.job_id;
      setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'processing', jobId: jobId, isRegenerating: false, fileName: isDefaultAudioName(q.fileName) ? audioCodeFromId(jobId) : q.fileName } : q));
      toast.info(`"${item.fileName}" ${t('audio.sent_processing')}`);
      await fetchUsage();
    } catch (error) {
      console.error('Erro ao gerar áudio:', error);
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || t('audio.queue_limit_error'));
        setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'error', errorMessage: error.response?.data?.detail || t('audio.queue_limit_error') } : q));
        await fetchUsage();
      } else if (error.response?.status === 429) {
        const retryAfter = error.response?.headers?.['retry-after'] || 60;
        toast.warning(t('audio.rate_limit_retry', { seconds: retryAfter }));
        setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'pending' } : q));
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.info(t('audio.request_queued'));
        setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'processing', isRegenerating: true } : q));
        // Timeout do POST: o job provavelmente foi criado no servidor. Recupera o
        // jobId real SEM reescrever a fila (antes era fetchHistory, destrutivo).
        await resolveOrphanFromHistory(item);
      } else {
        // Erro genérico/rede/500 APÓS o envio: o áudio costuma existir no servidor.
        // Regra do dono: NUNCA pintar falha numa geração enviada -> fica "Gerando"
        // (sem backendStatus:'failed') e o polling/self-heal acha no /history.
        toast.info(`"${item.fileName}" ${t('audio.sent_processing')}`);
        setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'processing', isRegenerating: true } : q));
        await resolveOrphanFromHistory(item);
      }
    } finally {
      setProcessingId(null);
    }
  };

  // Auto-process next pending
  useEffect(() => {
    if (processingId === null && usage.can_generate) {
      const nextPending = queue.find(q => q.status === 'pending');
      if (nextPending) {
        const timer = setTimeout(() => processQueueItem(nextPending), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [processingId, queue, usage.can_generate]);

  const generateAll = async () => {
    const pending = queue.filter(q => q.status === 'pending');
    for (let i = 0; i < pending.length; i++) {
      await processQueueItem(pending[i]);
      if (i < pending.length - 1) await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const removeFromQueue = async (id) => {
    const item = queue.find(q => q.id === id);
    if (item?.audioUrl && !item.audioUrl.startsWith('http')) URL.revokeObjectURL(item.audioUrl);
    setQueue(prev => prev.filter(q => q.id !== id));
    if (item?.fromHistory || item?.jobId) {
      try {
        const token = localStorage.getItem('token');
        const jobId = item.jobId || item.id;
        await axios.delete(`${BACKEND_URL}/api/audio-tts/history/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
      } catch (error) { console.error('Erro ao deletar do histórico:', error); }
    }
    setSelectedItems(prev => { const newSet = new Set(prev); newSet.delete(id); return newSet; });
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    // Get completed items on the CURRENT PAGE only
    const pageItems = queue.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    const completedOnPage = pageItems.filter(q => q.status === 'completed' && q.audioUrl);

    // Check if all items on current page are already selected
    const allPageSelected = completedOnPage.length > 0 && completedOnPage.every(q => selectedItems.has(q.id));

    if (allPageSelected) {
      // Deselect items on current page
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        completedOnPage.forEach(q => newSet.delete(q.id));
        return newSet;
      });
    } else {
      // Select all completed items on current page (add to existing selection)
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        completedOnPage.forEach(q => newSet.add(q.id));
        return newSet;
      });
    }
  };

  const clearSelection = () => setSelectedItems(new Set());

  // Trigger download helper
  const triggerDownload = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Download ZIP
  const downloadSelectedAsZip = async () => {
    let itemsToDownload = queue.filter(q => selectedItems.has(q.id) && q.audioUrl);
    if (itemsToDownload.length === 0) { toast.error(t('audio.no_selection_download')); return; }
    if (itemsToDownload.length > 100) { toast.error(t('audio.max_files_download')); return; }

    itemsToDownload = itemsToDownload.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });

    setIsDownloadingZip(true);
    setZipProgress({ current: 0, total: itemsToDownload.length });

    try {
      const zip = new JSZip();
      const token = localStorage.getItem('token');
      let downloadedFiles = 0;

      for (let i = 0; i < itemsToDownload.length; i++) {
        const item = itemsToDownload[i];
        setZipProgress({ current: i + 1, total: itemsToDownload.length });
        const fName = item.fileName || item.title || `darkplanner_audio_${i + 1}`;
        const orderNum = String(i + 1).padStart(2, '0');
        const safeFileName = `${orderNum}_${fName.replace(/[^a-zA-Z0-9_\-\s]/g, '_').substring(0, 50)}`;

        // Audio
        if (item.audioUrl) {
          try {
            let audioBuffer = null;
            try { const r = await fetch(item.audioUrl); if (r.ok) audioBuffer = await r.arrayBuffer(); } catch (e) {}
            if (!audioBuffer || audioBuffer.byteLength === 0) {
              try { const r = await fetch(`${BACKEND_URL}/api/proxy-download`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ url: item.audioUrl }) }); if (r.ok) audioBuffer = await r.arrayBuffer(); } catch (e) {}
            }
            if (audioBuffer && audioBuffer.byteLength > 0) { zip.file(`${safeFileName}.mp3`, audioBuffer); downloadedFiles++; }
          } catch (audioError) { console.error(`Erro ao baixar áudio ${fName}:`, audioError); }
        }
        // SRT
        const srtUrl = item.srt || item.srtUrl;
        if (srtUrl) {
          try {
            let srtBuffer = null;
            try { const r = await fetch(srtUrl); if (r.ok) srtBuffer = await r.arrayBuffer(); } catch (e) {}
            if (!srtBuffer || srtBuffer.byteLength === 0) { try { const r = await fetch(`${BACKEND_URL}/api/proxy-download`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ url: srtUrl }) }); if (r.ok) srtBuffer = await r.arrayBuffer(); } catch (e) {} }
            if (srtBuffer && srtBuffer.byteLength > 0) zip.file(`${safeFileName}.srt`, srtBuffer);
          } catch (srtError) { console.warn(`SRT não disponível para ${fName}`); }
        }
        // SRT por palavra (srtsyncpalavra.srt) e por tempo (srtsynctempo.srt)
        for (const extra of [{ url: item.srtPalavra, suffix: '_palavra' }, { url: item.srtTempo, suffix: '_tempo' }]) {
          if (!extra.url) continue;
          try {
            let buf = null;
            try { const r = await fetch(extra.url); if (r.ok) buf = await r.arrayBuffer(); } catch (e) {}
            if (!buf || buf.byteLength === 0) { try { const r = await fetch(`${BACKEND_URL}/api/proxy-download`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ url: extra.url }) }); if (r.ok) buf = await r.arrayBuffer(); } catch (e) {} }
            if (buf && buf.byteLength > 0) zip.file(`${safeFileName}${extra.suffix}.srt`, buf);
          } catch (e) { console.warn(`SRT ${extra.suffix} não disponível para ${fName}`); }
        }
      }

      if (downloadedFiles === 0) { toast.error(t('audio.no_files_downloaded')); return; }

      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      const timestamp = new Date().toISOString().slice(0, 10);
      saveAs(zipBlob, `audios_${timestamp}.zip`);
      toast.success(t('audio.zip_success', { count: Object.keys(zip.files).length }));
      clearSelection();
    } catch (error) {
      console.error('Erro ao criar ZIP:', error);
      toast.error(t('audio.zip_error'));
    } finally {
      setIsDownloadingZip(false);
      setZipProgress({ current: 0, total: 0 });
    }
  };

  // Individual download (3-layer)
  const downloadAudio = async (item) => {
    const audioUrl = item.audioUrl;
    if (!audioUrl) { toast.error(t('audio.audio_unavailable')); return; }
    if (item.createdAt) {
      const hoursPassed = (Date.now() - new Date(item.createdAt).getTime()) / (1000 * 60 * 60);
      if (hoursPassed >= 24) { toast.error(t('audio.audio_expired'), { duration: 4000 }); return; }
    }
    const safeName = (item.fileName || `darkplanner_audio_${item.jobId || item.id || 'download'}`).replace(/\.mp3$/, '') + '.mp3';
    try { const r = await fetch(audioUrl); if (!r.ok) throw new Error(); const blob = await r.blob(); if (!blob || blob.size === 0) throw new Error(); triggerDownload(blob, safeName); toast.success(t('audio.download_complete')); return; } catch (e) {}
    try { const token = localStorage.getItem('token'); const r = await fetch(`${BACKEND_URL}/api/proxy-download`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ url: audioUrl }) }); if (!r.ok) throw new Error(); const blob = await r.blob(); if (!blob || blob.size === 0) throw new Error(); triggerDownload(blob, safeName); toast.success(t('audio.download_complete')); return; } catch (e) {}
    try { const link = document.createElement('a'); link.href = audioUrl; link.download = safeName; document.body.appendChild(link); link.click(); document.body.removeChild(link); toast.success(t('audio.download_started')); } catch (e) { window.open(audioUrl, '_blank'); }
  };

  // SRT download (3-layer)
  const downloadSrt = async (item) => {
    const srtUrl = item.srt || item.srtUrl;
    if (!srtUrl) { toast.error(t('audio.srt_unavailable')); return; }
    if (item.createdAt) {
      const hoursPassed = (Date.now() - new Date(item.createdAt).getTime()) / (1000 * 60 * 60);
      if (hoursPassed >= 24) { toast.error(t('audio.srt_expired'), { duration: 4000 }); return; }
    }
    // Regra (owner): COM nome (digitado / arquivo / título) → "<nome>_<tipo>".
    // SEM nome (fallback darkplanner_audio_<código>) → "Darkplanner_<tipo>".
    // Tipos = MESMOS nomes dos arquivos no R2 (legenda/srtsyncpalavra/srtsynctempo).
    const SRT_R2_NAME = { palavra: 'srtsyncpalavra', tempo: 'srtsynctempo' };
    const tipo = item._suffix ? (SRT_R2_NAME[item._suffix] || item._suffix) : 'legenda';
    const hasUserName = item.fileName && !/^darkplanner_audio_/i.test(item.fileName);
    const safeName = hasUserName
      ? `${item.fileName.replace(/\.mp3$/i, '')}_${tipo}.srt`
      : `Darkplanner_${tipo}.srt`;
    try { const r = await fetch(srtUrl); if (!r.ok) throw new Error(); const blob = await r.blob(); if (!blob || blob.size === 0) throw new Error(); triggerDownload(blob, safeName); toast.success(t('audio.srt_downloaded')); return; } catch (e) {}
    try { const token = localStorage.getItem('token'); const r = await fetch(`${BACKEND_URL}/api/proxy-download`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ url: srtUrl }) }); if (!r.ok) throw new Error(); const blob = await r.blob(); if (!blob || blob.size === 0) throw new Error(); triggerDownload(blob, safeName); toast.success(t('audio.srt_downloaded')); return; } catch (e) {}
    try { const link = document.createElement('a'); link.href = srtUrl; link.download = safeName; document.body.appendChild(link); link.click(); document.body.removeChild(link); toast.success(t('audio.download_started')); } catch (e) { window.open(srtUrl, '_blank'); }
  };

  // Abre o Editor de SRT Tempo (mesmos guards dos downloads)
  const openSrtEditor = (item) => {
    if (!item.srtTempo && !item.srtVeo) { toast.error(t('audio.srt_unavailable')); return; }
    if (item.createdAt) {
      const hoursPassed = (Date.now() - new Date(item.createdAt).getTime()) / (1000 * 60 * 60);
      if (hoursPassed >= 24) { toast.error(t('audio.srt_expired'), { duration: 4000 }); return; }
    }
    setSrtEditorItem(item);
    setSrtEditorOpen(true);
  };

  // Play/pause result items — RULE: only one audio at a time
  const togglePlayItem = async (item) => {
    // Para o preview de voz (player compartilhado) ao tocar um resultado.
    vp.stop();

    if (playingItemId === item.id && isPlaying) {
      if (audioRef.current) audioRef.current.pause();
      setIsPlaying(false);
      vp.updateExternal({ isPlaying: false });
      return;
    }
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    // Barra inferior (mesma do preview): controles delegados ao <audio> de resultado.
    const barName = item.voiceName || item.voice_name || item.voiceLabel || t('audio.result_title');
    const barCtl = {
      toggle: () => {
        const a = audioRef.current; if (!a) return;
        if (a.paused) { a.play().then(() => { setIsPlaying(true); vp.updateExternal({ isPlaying: true }); }).catch(() => {}); }
        else { a.pause(); setIsPlaying(false); vp.updateExternal({ isPlaying: false }); }
      },
      seek: (s) => { const a = audioRef.current; if (a && Number.isFinite(s)) { a.currentTime = Math.max(0, Math.min(s, a.duration || s)); } },
      skip: (d) => { const a = audioRef.current; if (a) { a.currentTime = Math.max(0, Math.min((a.currentTime || 0) + d, a.duration || ((a.currentTime || 0) + d))); } },
      stop: () => { const a = audioRef.current; if (a) a.pause(); setIsPlaying(false); setPlayingItemId(null); },
    };
    const showBar = (url) => vp.showExternal({ name: barName, url, isPlaying: true, currentTime: 0, duration: item.duration || 0 }, barCtl);

    if (item.audioUrl && item.audioUrl.startsWith('http')) {
      if (audioRef.current) {
        audioRef.current.src = item.audioUrl;
        try { await audioRef.current.play(); setPlayingItemId(item.id); setIsPlaying(true); showBar(item.audioUrl); } catch (e) { toast.error(t('audio.audio_play_error')); }
      }
      return;
    }
    if (item.audioBlob) {
      const url = URL.createObjectURL(item.audioBlob);
      if (audioRef.current) {
        audioRef.current.src = url;
        try { await audioRef.current.play(); setPlayingItemId(item.id); setIsPlaying(true); showBar(url); } catch (e) { toast.error(t('audio.audio_play_error')); }
      }
      return;
    }
    const jobId = item.jobId || item.id;
    if (jobId) {
      try {
        toast.info(t('audio.audio_loading'));
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/audio-tts/job/${jobId}/download`, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.audio_url && audioRef.current) {
          audioRef.current.src = response.data.audio_url;
          try { await audioRef.current.play(); setPlayingItemId(item.id); setIsPlaying(true); showBar(response.data.audio_url); } catch (e) { toast.error(t('audio.audio_play_error')); }
        } else { toast.error(t('audio.audio_url_missing')); }
      } catch (error) { toast.error(error.response?.data?.detail || t('audio.audio_load_error')); }
    }
  };

  // Clear completed
  const clearCompleted = async () => {
    queue.filter(q => q.status === 'completed').forEach(item => {
      if (item.audioUrl && !item.audioUrl.startsWith('http')) URL.revokeObjectURL(item.audioUrl);
    });
    setQueue(prev => prev.filter(q => q.status !== 'completed'));
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/audio-tts/history/clear/completed`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('audio.history_cleared'));
    } catch (error) { console.error('Erro ao limpar histórico:', error); }
  };

  // Helper: exclui da queue apenas os items SELECIONADOS (chama removeFromQueue
  // pra cada — que cuida do DELETE no backend + revoke URL objects).
  // Reutiliza clearSelection() já declarada acima (linha 1227).
  const deleteSelected = async () => {
    const ids = Array.from(selectedItems);
    setSelectedItems(new Set());
    for (const id of ids) {
      try {
        await removeFromQueue(id);
      } catch (e) {
        console.error('Erro ao excluir item selecionado:', id, e);
      }
    }
  };

  // File upload
  const handleSingleFileUpload = async (file) => {
    if (!file) return;
    const extension = file.name.split('.').pop().toLowerCase();
    if (!['txt', 'docx'].includes(extension)) { toast.error(t('audio.unsupported_format')); return; }
    try {
      if (extension === 'txt') { const text = await file.text(); setText(text); setFileName(file.name.replace(/\.[^/.]+$/, '')); }
      else if (extension === 'docx') { toast.info(t('audio.docx_wip')); }
    } catch (error) { toast.error(t('audio.file_read_error', { name: file.name })); }
  };

  const handleMultipleFilesUpload = async (files) => {
    if (!files || files.length === 0) return;
    if (!selectedVoice) { toast.error(t('audio.select_voice_first')); return; }
    const voiceData = voices.find(v => v.id === selectedVoice);
    let addedCount = 0;
    for (const file of files) {
      const extension = file.name.split('.').pop().toLowerCase();
      if (!['txt', 'docx'].includes(extension)) { toast.error(t('audio.file_unsupported', { name: file.name })); continue; }
      try {
        let fileText = '';
        if (extension === 'txt') fileText = await file.text();
        else if (extension === 'docx') { toast.info(t('audio.file_docx_wip', { name: file.name })); continue; }
        if (fileText.trim()) {
          const newItem = { id: Date.now().toString() + Math.random().toString(36).substr(2, 9), text: fileText.trim(), fullText: fileText.trim(), textLength: fileText.trim().length, voiceId: selectedVoice, voiceName: voiceData?.name || 'Voz', voiceData: voiceData, fileName: file.name.replace(/\.[^/.]+$/, ''), speed: speed, status: 'pending', audioBlob: null, audioUrl: null, srt: null, createdAt: new Date() };
          setQueue(prev => [newItem, ...prev]);
          addedCount++;
        }
      } catch (error) { toast.error(t('audio.file_read_error', { name: file.name })); }
    }
    if (addedCount > 0) { toast.success(t('audio.files_added', { count: addedCount })); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault(); setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 1) handleSingleFileUpload(files[0]);
    else if (files.length > 1) handleMultipleFilesUpload(files);
  };

  // Close settings popover on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSettingsPopover && settingsBtnRef.current && settingsPopupRef.current &&
        !settingsBtnRef.current.contains(e.target) && !settingsPopupRef.current.contains(e.target)) {
        setShowSettingsPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettingsPopover]);

  // ═══════════════════════════════════════════════════════════════════════
  // COMPUTED
  // ═══════════════════════════════════════════════════════════════════════
  // Variações materializadas como "pseudo-vozes": clone da voz base com o nome
  // customizado por cima. Mantém o MESMO id (gerar áudio usa a voz base);
  // a chave única de lista é variation_id.
  const variationVoices = useMemo(() => {
    if (!variations.length || !voices.length) return [];
    const byId = new Map(voices.map((v) => [v.id, v]));
    return variations
      .map((va) => {
        const base = byId.get(va.voice_id);
        if (!base) return null; // voz saiu do catálogo
        return {
          ...base,
          name: va.custom_name,
          original_name: va.original_name || base.name,
          is_variation: true,
          variation_id: va.id,
        };
      })
      .filter(Boolean);
  }, [variations, voices]);

  // Idiomas disponíveis pro filtro (código ISO + label PT), ordenados por label
  const voiceLanguageOptions = useMemo(() => {
    const set = new Set();
    for (const v of voices) {
      for (const lang of getLanguagesOfVoice(v)) set.add(lang);
    }
    const opts = Array.from(set)
      .map((code) => ({
        value: code,
        label: translate(code, LANGUAGE_PT) || code,
        flag: getFlagCode(code, null),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    return [{ value: 'all', label: t('audio.all_languages') }, ...opts];
  }, [voices, t]);

  // Busca: matchesSearch (multi-token, sem acento, nome + slug + idiomas +
  // categoria/uso traduzidos) — resolve "nome completo não acha". Variações
  // entram em "Minhas Vozes" (♡), no topo da lista.
  const filteredVoices = useMemo(() => {
    const matchesLang = (v) =>
      voiceLangFilter === 'all' || getLanguagesOfVoice(v).includes(voiceLangFilter);
    // Voz NATIVA do idioma filtrado = idioma primário dela é o filtrado
    // (não só suportado via verified_languages)
    const isNative = (v) =>
      (((v.voice_info && v.voice_info.language) || v.language || '').toLowerCase() === voiceLangFilter);
    // Sort estável: nativas do idioma primeiro, demais mantêm a ordem original
    const nativeFirst = (list) =>
      voiceLangFilter === 'all'
        ? list
        : list.sort((a, b) => (isNative(b) ? 1 : 0) - (isNative(a) ? 1 : 0));
    const base = nativeFirst(voices.filter(v => {
      if (!matchesSearch(v, searchTerm)) return false;
      if (!matchesLang(v)) return false;
      switch (voiceFilter) {
        case 'favorites': return favorites.includes(v.id);
        case 'female': return v.gender === 'female';
        case 'male': return v.gender === 'male';
        default: return true;
      }
    }));
    if (voiceFilter !== 'favorites') return base;
    const vars = nativeFirst(variationVoices.filter((v) => matchesSearch(v, searchTerm) && matchesLang(v)));
    return [...vars, ...base];
  }, [voices, variationVoices, searchTerm, voiceFilter, voiceLangFilter, favorites]);

  // PERFORMANCE — virtualização da lista de vozes: renderiza só ~visíveis (não 500+)
  // e não re-renderiza todos a cada tecla na textarea. Altura DINÂMICA (measureElement):
  // cada card mantém EXATAMENTE a altura de hoje (1 ou 2 linhas). Visual idêntico.
  const voiceListRef = useRef(null);
  const voiceVirtualizer = useVirtualizer({
    count: filteredVoices.length,
    getScrollElement: () => voiceListRef.current,
    estimateSize: () => 52,
    overscan: 8,
    // Variações compartilham o id da voz base — a chave única é variation_id
    getItemKey: (index) => filteredVoices[index]?.variation_id ?? filteredVoices[index]?.id ?? index,
  });

  const selectedVoiceData = voices.find(v => v.id === selectedVoice);
  const pendingCount = queue.filter(q => q.status === 'pending').length;
  const processingCount = queue.filter(q => q.status === 'processing').length;
  const completedCount = queue.filter(q => q.status === 'completed').length;
  // Total credits effectively committed: backend-confirmed (usage.used) + currently processing (not yet counted by backend)
  const effectiveUsed = usage.used + processingCount;

  // ═══════════════════════════════════════════════════════════════════════
  // GENERATE HANDLER
  // ═══════════════════════════════════════════════════════════════════════
  const handleGenerate = async () => {
    if (!text.trim() || !selectedVoice) return;
    setRightTab('history'); // mostra o progresso/Histórico ao gerar
    // ─── MULTIVOZ: extrai slugs literais do texto e valida (sem auto-substituição) ──
    // Usuário escreve direto com slugs reais (ex: "[mark] olá [aami] tudo bem").
    // Slugs únicos são extraídos via regex e enviados em narrators_data.
    let multivoiceSlugs = [];
    if (isMultivoiceMode) {
      multivoiceSlugs = [...new Set(
        text.match(/\[([a-z0-9_]+)\]/gi)?.map((s) => s.slice(1, -1).toLowerCase()) || []
      )];
      if (multivoiceSlugs.length === 0) {
        toast.error('Adicione tags [slug] no texto. Ex: [mark] olá [aami] tudo bem');
        return;
      }
      if (multivoiceSlugs.length > 6) {
        toast.error('Máximo 6 vozes diferentes');
        return;
      }
      if (!usage.unlimited && (usage.used + multivoiceSlugs.length) > usage.limit) {
        toast.error(`Este áudio precisa de ${multivoiceSlugs.length} créditos. Você tem ${Math.max(0, usage.limit - usage.used)} disponíveis.`);
        return;
      }
    }
    const charLimit = usage.character_limit || 80000;
    const hasUnlimitedChars = usage.unlimited || usage.character_limit === -1;
    if (!hasUnlimitedChars && text.length > charLimit) {
      toast.error(t('audio.text_too_long', { length: text.length.toLocaleString('pt-BR'), limit: charLimit.toLocaleString('pt-BR') }));
      return;
    }
    if (!usage.can_generate) {
      // FASE 4: Free = 5 vitalícios esgotados (não diário); pagante = limite diário.
      if (usage.is_free_lifetime) {
        toast.error(t('audio.free_limit_short'));
        try { window.dispatchEvent(new CustomEvent('premium-required', { detail: { feature: 'audio_free' } })); } catch {}
      } else {
        toast.error(t('audio.daily_limit_short', { used: usage.used, limit: usage.limit }));
      }
      return;
    }
    const pendingInQueue = queue.filter(q => q.status === 'pending' || q.status === 'processing').length;
    if (pendingInQueue >= usage.remaining && usage.remaining > 0) {
      toast.error(t('audio.queue_credits', { pending: pendingInQueue, remaining: usage.remaining }));
      return;
    }
    const roteiroVideoId = selectedRoteiro || null;
    const voiceData = voices.find(v => v.id === selectedVoice);
    const itemFileName = fileName || `darkplanner_audio_${shortAudioCode()}`;
    const newItem = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      text: text.trim(), fullText: text.trim(), textLength: text.trim().length,
      voiceId: selectedVoice, voiceName: voiceData?.name || 'Voz', voiceData: voiceData,
      fileName: itemFileName, speed: speed, status: 'processing', audioBlob: null, audioUrl: null,
      srt: null, createdAt: new Date(), fromRoteiro: roteiroVideoId
    };
    setQueue(prev => [newItem, ...prev]);
    setText('');
    setFileName('');
    setSelectedRoteiro('');
    // ─── MULTIVOZ: payload com slugs literais (texto vai sem substituição) ──
    const multivoicePayload = isMultivoiceMode
      ? {
          is_multivoice: true,
          narrators_count: multivoiceSlugs.length,
          narrators_data: multivoiceSlugs.map((s) => ({ voice_slug: s })),
          original_text: newItem.text,
        }
      : {};
    try {
      const token = localStorage.getItem('token');
      const deviceFp = await getDeviceFp();  // ETAPA 3: antifraude do free (pagante ignora)
      const response = await axios.post(`${BACKEND_URL}/api/audio-tts/generate`, {
        text: newItem.text, voice_id: newItem.voiceId, title: newItem.fileName,
        use_cache: useCache, include_srt: includeSrt, async_mode: true,
        video_id: roteiroVideoId, speed: speed, volume: volume,
        subtitle_words: subtitleWords, subtitle_case: subtitleCase,
        subtitle_veo_words: subtitleVeoWords,
        subtitle_veo_min: subtitleVeoMin, subtitle_veo_max: subtitleVeoMax,
        device_fp: deviceFp || undefined,
        ...multivoicePayload
      }, { headers: { Authorization: `Bearer ${token}` } });  // SEM timeout (idem processQueueItem)
      if (response.data.success) {
        const backendId = response.data.job_id;
        // Sem título: troca o código provisório pelo definitivo (job_id, único).
        setQueue(prev => prev.map(q => q.id === newItem.id ? { ...q, jobId: backendId, id: backendId, fileName: isDefaultAudioName(q.fileName) ? audioCodeFromId(backendId) : q.fileName } : q));
        toast.success(t('audio.sent_to_queue', { name: itemFileName }));
      }
    } catch (error) {
      console.error('Erro ao enviar para fila:', error);
      const st = error.response?.status;
      if (st === 403 || st === 429) {
        // Limite/permissão: barra ANTES de gerar -> mantém o aviso ao usuário (NÃO entra na regra).
        setQueue(prev => prev.map(q => q.id === newItem.id ? { ...q, status: 'error', errorMessage: error.response?.data?.detail || t('audio.queue_add_error') } : q));
        toast.error(error.response?.data?.detail || t('audio.queue_add_error'));
      } else {
        // Timeout/rede/500 APÓS o envio: o áudio costuma existir no servidor.
        // Regra do dono: NUNCA pintar falha numa geração enviada -> fica "Gerando"
        // e o polling/self-heal (matchHistoryItem) acha no /history.
        toast.info(t('audio.sent_to_queue', { name: itemFileName }));
        setQueue(prev => prev.map(q => q.id === newItem.id ? { ...q, status: 'processing', isRegenerating: true } : q));
        await resolveOrphanFromHistory(newItem);
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // PAINEL DE CONFIGURAÇÕES (inline na aba direita) — reusa todo o estado.
  // SRT repaginado em 3 cards claros, com exemplo ao vivo do texto do usuário.
  // ═══════════════════════════════════════════════════════════════════════
  const _srtSample = (text.trim().replace(/<break[^>]*\/?>/gi, ' ').match(/\S+/g)
    || ['Você', 'já', 'tentou', 'parar', 'com', 'alguma', 'coisa', 'na', 'vida']).slice(0, 12);
  const _srtExample = (n) => {
    const a = _srtSample.slice(0, n).join(' ');
    const b = _srtSample.slice(n, n * 2).join(' ');
    return b ? `${a} / ${b}` : a;
  };
  // Preenchimento azul (estilo Fish) p/ sliders nativos: gradiente até o valor.
  const _fillBg = (pct) => {
    const p = Math.max(0, Math.min(100, pct));
    return `linear-gradient(90deg, var(--dp-blue-500) 0%, var(--dp-blue-500) ${p}%, var(--dp-border-medium) ${p}%, var(--dp-border-medium) 100%)`;
  };
  const _srtMarker = (label, color) => (
    <span style={{
      width: '22px', height: '22px', borderRadius: '7px', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: `${color}1A`, color, fontSize: '11px', fontWeight: 700,
    }}>{label}</span>
  );
  const _srtCard = (marker, title, valueLabel, desc, control, example, opts = {}) => (
    <div style={{
      background: opts.highlight ? 'rgba(13,174,255,0.06)' : 'var(--dp-bg-card-hover)',
      border: `1px solid ${opts.highlight ? 'rgba(13,174,255,0.22)' : 'var(--dp-border-subtle)'}`,
      borderRadius: '12px', padding: '12px 13px', marginBottom: '9px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        {marker}
        <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>{title}</span>
        {opts.badge && (
          <span style={{
            fontSize: '8.5px', fontWeight: 700, color: '#fff', letterSpacing: '0.5px',
            background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)', padding: '2px 7px', borderRadius: '9999px',
          }}>{opts.badge}</span>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--dp-blue-500)', whiteSpace: 'nowrap' }}>{valueLabel}</span>
      </div>
      <div style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', lineHeight: 1.45, marginBottom: '9px' }}>{desc}</div>
      {control}
      {example && (
        <div style={{
          marginTop: '9px', fontSize: '11px', color: 'var(--dp-text-secondary)',
          background: 'rgba(255,255,255,0.04)', borderRadius: '7px', padding: '6px 9px',
          fontFamily: 'var(--dp-font)', lineHeight: 1.4,
        }}>
          <span style={{ color: 'var(--dp-text-tertiary)' }}>ex.: </span>{example}
        </div>
      )}
    </div>
  );

  const settingsPanel = (
    <div className="ag-scrollbar ag-settings" style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '4px 2px 8px' }}>
      {/* ── LEGENDA (SRT) — vem PRIMEIRO: uso principal (sincronização + prompts) ── */}
      <div className="ag-cfg-full" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 2px 10px' }}>
        <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px' }}>
          {t('audio.subtitle_section') || 'Legenda (SRT)'}
        </span>
        <button
          onClick={() => setIncludeSrt(!includeSrt)}
          style={{
            width: '34px', height: '19px', borderRadius: '10px', border: 'none', cursor: 'pointer',
            position: 'relative', flexShrink: 0,
            background: includeSrt ? 'var(--dp-blue-500)' : 'var(--dp-border-medium)', transition: 'background 0.2s',
          }}
        >
          <div style={{
            position: 'absolute', width: '15px', height: '15px', borderRadius: '50%', background: 'white', top: '2px',
            right: includeSrt ? '2px' : 'auto', left: includeSrt ? 'auto' : '2px', transition: 'all 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }} />
        </button>
      </div>

      {includeSrt && (
        <>
          <div className="ag-cfg-full" style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', lineHeight: 1.4, margin: '0 2px 10px' }}>
            {t('audio.srt_intro') || 'Geramos 3 formatos de legenda. Ajuste cada um conforme seu editor:'}
          </div>

          {/* PRINCIPAL: Por tempo (VEO) — sincronização + prompts por bloco */}
          {_srtCard(
            _srtMarker('⏱', '#0DAEFF'),
            t('audio.subtitle_tempo'),
            `${subtitleVeoMin}–${subtitleVeoMax}s`,
            t('audio.srt_desc_tempo') || 'Uso exclusivo para SINCRONIZAÇÃO: divide a narração em blocos de X segundos. Você cria os prompts por bloco e depois sincroniza com os vídeos (VEO/Shorts).',
            <div style={{ position: 'relative', height: '16px', display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: '6px', background: 'var(--dp-border-medium)', borderRadius: '9999px' }} />
              <div style={{
                position: 'absolute', height: '6px', borderRadius: '9999px', top: '50%', transform: 'translateY(-50%)',
                background: 'linear-gradient(90deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))',
                left: `${((subtitleVeoMin - 1) / 24) * 100}%`, right: `${100 - ((subtitleVeoMax - 1) / 24) * 100}%`,
              }} />
              <input type="range" min="1" max="25" step="1" value={subtitleVeoMin}
                onChange={(e) => setSubtitleVeoMin(Math.min(parseInt(e.target.value), subtitleVeoMax))}
                className="ag-range-dual" style={{ zIndex: 3 }} aria-label={`${t('audio.veo_duration')} (min)`} />
              <input type="range" min="1" max="25" step="1" value={subtitleVeoMax}
                onChange={(e) => setSubtitleVeoMax(Math.max(parseInt(e.target.value), subtitleVeoMin))}
                className="ag-range-dual" style={{ zIndex: 4 }} aria-label={`${t('audio.veo_duration')} (max)`} />
            </div>,
            null,
            { highlight: true, badge: t('audio.srt_tempo_badge') || 'VEO · SYNC' },
          )}

          {/* Por frase natural */}
          {_srtCard(
            _srtMarker('¶', '#0DAEFF'),
            t('audio.subtitle_legenda'),
            `${t('audio.max_label')} ${subtitleWords}`,
            t('audio.srt_desc_legenda') || 'Quebra no ritmo da fala, respeitando pausas. Leitura confortável.',
            <input type="range" min="1" max="15" step="1" value={subtitleWords} onChange={(e) => setSubtitleWords(parseInt(e.target.value))} className="ag-slider" style={{ width: '100%', background: _fillBg(((subtitleWords - 1) / 14) * 100) }} />,
            null,
          )}

          {/* Nº fixo de palavras */}
          {_srtCard(
            _srtMarker('#', '#0DAEFF'),
            t('audio.subtitle_palavra'),
            `${subtitleVeoWords}`,
            t('audio.srt_desc_palavra') || 'Toda linha com a MESMA quantidade de palavras. Visual uniforme.',
            <input type="range" min="1" max="15" step="1" value={subtitleVeoWords} onChange={(e) => setSubtitleVeoWords(parseInt(e.target.value))} className="ag-slider" style={{ width: '100%', background: _fillBg(((subtitleVeoWords - 1) / 14) * 100) }} />,
            null,
          )}

          {/* Maiúsculas — linha única compacta (label + 3 botões pequenos),
              encurta o bloco e sobe a seção Áudio */}
          <div style={{
            marginTop: '4px', marginBottom: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
          }}>
            <div style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', marginLeft: '2px' }}>{t('audio.format')}</div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[
                { id: 'normal', label: 'Aa', tip: t('audio.format_tip_normal') },
                { id: 'upper', label: 'AA', tip: t('audio.format_tip_upper') },
                { id: 'lower', label: 'aa', tip: t('audio.format_tip_lower') },
              ].map(opt => (
                <DarkTooltip key={opt.id} content={opt.tip} position="top" maxWidth={220}>
                  <button onClick={() => setSubtitleCase(opt.id)}
                    style={{
                      padding: '4px 11px', textAlign: 'center', borderRadius: '7px',
                      border: subtitleCase === opt.id ? '1px solid var(--dp-blue-500)' : '1px solid var(--dp-border-subtle)',
                      background: subtitleCase === opt.id ? 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))' : 'var(--dp-bg-card-hover)',
                      color: subtitleCase === opt.id ? 'white' : 'var(--dp-text-secondary)',
                      fontSize: '11px', fontWeight: 600, fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.25s',
                    }}>
                    {opt.label}
                  </button>
                </DarkTooltip>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── ÁUDIO (embaixo) — linhas estilo Fish: rótulo + slider + valor ── */}
      <div className="ag-cfg-full" style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', margin: '2px 2px 8px' }}>
        {t('audio.audio_section') || 'Áudio'}
      </div>
      {[
        { key: 'speed', label: t('audio.speed'), value: `${speed.toFixed(1)}x`, min: 0.5, max: 1.5, step: 0.1, val: speed, set: (v) => setSpeed(v) },
        { key: 'volume', label: t('audio.volume'), value: `${volume.toFixed(1)}x`, min: 0.5, max: 2, step: 0.1, val: volume, set: (v) => setVolume(v) },
      ].map((row) => (
        <div key={row.key} style={{
          background: 'var(--dp-bg-card-hover)', border: '1px solid var(--dp-border-subtle)',
          borderRadius: '10px', padding: '9px 12px', marginBottom: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '7px' }}>
            <span style={{ fontSize: '12.5px', color: 'var(--dp-text-primary)', fontWeight: 500 }}>{row.label}</span>
            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--dp-blue-500)' }}>{row.value}</span>
          </div>
          <input type="range" min={row.min} max={row.max} step={row.step} value={row.val}
            onChange={(e) => row.set(parseFloat(e.target.value))} className="ag-slider"
            style={{ width: '100%', background: _fillBg(((row.val - row.min) / (row.max - row.min)) * 100) }} />
        </div>
      ))}

      {/* Reset + Salvar como padrão */}
      <div className="ag-cfg-full" style={{ height: '1px', background: 'var(--dp-border-subtle)', margin: '12px 2px' }} />
      <div className="ag-cfg-full" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2px' }}>
        <button
          onClick={() => {
            setSpeed(1.0); setVolume(1.0); setIncludeSrt(true);
            setSubtitleWords(5); setSubtitleCase('normal'); setSubtitleVeoWords(5);
            setSubtitleVeoMin(7); setSubtitleVeoMax(8); setConfigSource('system');
          }}
          style={{
            background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none',
            WebkitAppearance: 'none', appearance: 'none',
            cursor: 'pointer', fontSize: '11px',
            color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', padding: 0,
            textDecoration: 'underline', textUnderlineOffset: '2px',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
        >
          {t('audio.reset') || 'Resetar'}
        </button>
        <button
          onClick={() => saveAsDefault()}
          disabled={savingPrefs}
          style={{
            display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 13px', borderRadius: '9999px',
            background: savingPrefs ? 'var(--dp-border-medium)' : 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))',
            border: 'none', color: 'white', fontSize: '11px', fontWeight: 600, fontFamily: 'var(--dp-font)',
            cursor: savingPrefs ? 'not-allowed' : 'pointer', transition: 'all 0.25s', opacity: savingPrefs ? 0.6 : 1,
          }}
        >
          <Save size={11} />
          {savingPrefs ? (t('audio.saving') || 'Salvando...') : (t('audio.save_default') || 'Salvar como padrão')}
        </button>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div className="dp-page" style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-page)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        transition: 'margin-left 0.3s ease',
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        fontFamily: 'var(--dp-font)',
      }}>
        {/* SVG oculto: gradiente da marca p/ preencher ícones (ver DESIGN_SYSTEM.md) */}
        <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
          <defs>
            <linearGradient id="dpIconGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#0DAEFF" />
              <stop offset="100%" stopColor="#3B82F6" />
            </linearGradient>
          </defs>
        </svg>

        <div className="dp-page-inner">
          {/* ════════ TÍTULO NO TOPO (ícone preenchido com o degradê da marca) ════════ */}
          <div className="dp-page-head">
            <div style={{
              width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
              boxShadow: '0 4px 14px rgba(13,174,255,0.28)',
            }}>
              <Mic size={19} color="#fff" strokeWidth={2.2} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h1 className="dp-page-head__title">{t('nav.audio_generator')}</h1>
              <p className="dp-page-head__sub">{t('audio.description')}</p>
            </div>
            {/* Botão de layout: INLINE na mesma linha do badge (não cresce o header). */}
            <button
              type="button"
              onClick={toggleLayout}
              title={wideResults ? t('audio.layout_columns', '3 colunas') : t('audio.layout_bottom', 'Resultado embaixo')}
              aria-label={wideResults ? t('audio.layout_columns', '3 colunas') : t('audio.layout_bottom', 'Resultado embaixo')}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 8, cursor: 'pointer', flexShrink: 0,
                background: 'transparent', border: '1px solid var(--dp-border-subtle)',
                color: 'var(--dp-text-secondary)', transition: 'border-color 0.15s, color 0.15s',
              }}
            >
              {wideResults ? <Columns3 size={15} /> : <PanelBottom size={15} />}
            </button>
            <span style={{
              fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)',
              background: 'rgba(13,174,255,0.08)', border: '1px solid rgba(13,174,255,0.22)',
              padding: '5px 13px', borderRadius: '9999px', whiteSpace: 'nowrap',
              fontFamily: 'var(--dp-font)', letterSpacing: '0.3px',
            }}>
              {usage.unlimited ? `⚡ ${t('audio.unlimited')}` : (usage.limit == null ? '…' : `${effectiveUsed}/${usage.limit}`)}
            </span>
          </div>

          {/* ════════ 3-COLUMN LAYOUT ════════ */}
          {/* Esquerda 300 / direita 340; alignItems:'start' pra cada coluna ter
              altura própria. Responsivo via container queries (@container
              main-content) definidas no <style> ao final do componente. */}
          <div className={`audio-layout-grid${wideResults ? ' ag-two-col' : ''}`} style={{
            display: 'grid',
            gridTemplateColumns: '300px minmax(0, 1fr) 340px',
            gap: '20px',
            alignItems: 'start',
            animation: 'agFadeInUp 0.35s ease',
          }}>
          {/* ═══════════ LEFT: VOICE PANEL ═══════════ */}
          <div style={{
            background: 'var(--dp-bg-card)',
            border: '1px solid var(--dp-border-subtle)',
            borderRadius: 'var(--dp-radius-md, 12px)',
            display: 'flex',
            flexDirection: 'column',
            // Piso de 560px: em tela baixa/zoom alto o 100vh encolhe; sem piso a
            // coluna esmagava o conteúdo (a página passa a rolar, padrão Kanban).
            height: 'max(calc(100vh - 220px), 560px)',
            overflow: 'hidden',
            position: 'relative',
          }}>
            {/* Gradient top bar — padrão visual DarkPlanner (blue→purple→pink).
                Unifica visualmente as 3 colunas do TTS. Mesmo gradient/opacity
                do "Selected voice card" da coluna central. */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
              background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)',
              opacity: 0.6,
              borderRadius: 'var(--dp-radius-md, 12px) var(--dp-radius-md, 12px) 0 0',
              zIndex: 1,
            }} />
            {/* Voice panel header */}
            <div style={{ padding: '12px 12px 10px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', color: 'var(--dp-text-primary)' }}>
                {t('audio.voice_library')}
                <span style={{
                  background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                  color: '#FFFFFF',
                  fontSize: '10px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '9999px',
                }}>{voices.length}</span>
                <div style={{ flex: 1 }} />
                <DarkTooltip content="Abrir biblioteca completa de vozes" position="bottom" delay={400}>
                  <button
                    onClick={() => setLibraryModalOpen(true)}
                    style={{
                      padding: '4px 9px',
                      borderRadius: '9999px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                      color: '#FFFFFF',
                      fontSize: '10px',
                      fontWeight: 600,
                      fontFamily: 'var(--dp-font)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s',
                      letterSpacing: '0.2px',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #1FB8FF 0%, #4F8FF8 100%)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)'; }}
                  >
                    <LayoutGrid size={14} />
                    Vozes
                  </button>
                </DarkTooltip>
              </div>

              {/* Search */}
              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--dp-text-tertiary)' }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('audio.search_voice')}
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 32px',
                    background: 'var(--dp-bg-input, var(--dp-bg-primary))',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: 'var(--dp-radius-sm, 8px)',
                    color: 'var(--dp-text-primary)',
                    fontFamily: 'var(--dp-font)',
                    fontSize: '12px',
                    outline: 'none',
                    transition: 'all 0.3s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500)'; e.target.style.boxShadow = '0 0 0 3px rgba(13, 174, 255, 0.25)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              {/* Filter pills — Todos | Minhas Vozes | F | M (F/M compactos) */}
              <div style={{ display: 'flex', gap: '5px', marginBottom: '8px' }}>
                {[
                  { id: 'all', label: t('audio.filter_all') },
                  { id: 'favorites', label: t('audio.filter_my_voices') },
                  { id: 'female', label: 'F', compact: true },
                  { id: 'male', label: 'M', compact: true },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setVoiceFilter(tab.id)}
                    style={{
                      flex: tab.compact ? '0 0 34px' : 1,
                      padding: '6px 0',
                      whiteSpace: 'nowrap',
                      borderRadius: '9999px',
                      border: voiceFilter === tab.id ? '1px solid transparent' : '1px solid var(--dp-border-subtle)',
                      background: voiceFilter === tab.id
                        ? 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)'
                        : 'transparent',
                      color: voiceFilter === tab.id ? '#FFFFFF' : 'var(--dp-text-tertiary)',
                      fontSize: '11px',
                      fontWeight: voiceFilter === tab.id ? 600 : 500,
                      cursor: 'pointer',
                      transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                      fontFamily: 'var(--dp-font)',
                      textAlign: 'center',
                      boxShadow: 'none',
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Filtro de idioma — dropdown fino com bandeiras (padrão preview) */}
              <LanguageFilterDropdown
                options={voiceLanguageOptions}
                value={voiceLangFilter}
                onChange={setVoiceLangFilter}
              />
            </div>

            {/* Legenda dos selos de qualidade (verificado dourado/preto) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '0 12px 9px', flexShrink: 0 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: 'var(--dp-text-tertiary)' }}>
                <BadgeCheck size={13} fill="#FFB300" color="#FFFFFF" strokeWidth={2.5} /> {t('audio.badge_professional') || 'Profissional'}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: 'var(--dp-text-tertiary)' }}>
                <BadgeCheck size={13} fill="#1F2937" color="#FFFFFF" strokeWidth={2.5} /> {t('audio.badge_quality') || 'Alta qualidade'}
              </span>
            </div>

            {/* Voice list */}
            <div ref={voiceListRef} style={{
              flex: 1,
              overflowY: 'auto',
              padding: '6px 8px',
            }} className="ag-scrollbar">
              {loadingVoices ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '4px 0' }}>
                  {[1,2,3,4,5,6,7,8].map(i => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px' }}>
                      <div className="dp-skeleton" style={{ width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div className="dp-skeleton" style={{ width: `${60 + (i * 5) % 30}%`, height: '12px', marginBottom: '5px' }} />
                        <div className="dp-skeleton" style={{ width: '50%', height: '9px' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredVoices.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 15px', color: 'var(--dp-text-tertiary)' }}>
                  <Heart size={32} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
                  <p style={{ fontSize: '12px' }}>{voiceFilter === 'favorites' ? t('audio.no_favorites') : t('audio.no_voices')}</p>
                </div>
              ) : (
                // Container de altura total (todos os itens) — itens visíveis posicionados
                // por translateY. Card e props IDÊNTICOS ao de antes; só não monta os 500.
                <div style={{ height: `${voiceVirtualizer.getTotalSize()}px`, position: 'relative', width: '100%' }}>
                  {voiceVirtualizer.getVirtualItems().map((vRow) => {
                    const voice = filteredVoices[vRow.index];
                    const isSelected = selectedVoice === voice.id;
                    const isPreviewing = previewingVoiceId === voice.id;
                    const isFav = favorites.includes(voice.id);
                    const isMultivoiceVoice = voice.name === 'Multivoz';
                    return (
                      <div
                        key={vRow.key}
                        data-index={vRow.index}
                        ref={voiceVirtualizer.measureElement}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${vRow.start}px)` }}
                      >
                        <VoiceCardCompact
                          voice={voice}
                          isSelected={isSelected}
                          isFavorite={isFav}
                          isPreviewing={isPreviewing}
                          isMultivoz={isMultivoiceVoice}
                          onSelect={(v) => setSelectedVoice(v.id)}
                          onToggleFavorite={toggleFavorite}
                          onCreateVariation={(v) => setVariationModal({ mode: 'create', voice: v })}
                          onEditVariation={(v) => {
                            const variation = variations.find((x) => x.id === v.variation_id);
                            if (variation) setVariationModal({ mode: 'edit', variation });
                          }}
                          onDeleteVariation={deleteVariation}
                          avatar={
                            <VoiceAvatar
                              voice={voice}
                              size={30}
                              isPreviewing={isPreviewing}
                              onPlayClick={getPreviewUrl(voice) ? () => toggleVoicePreview(voice) : undefined}
                            />
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ═══════════ CENTER: EDITOR PANEL ═══════════ */}
          {/* Container externo da Coluna 2 — agora é UM card visual único (igual
              Cols 1 e 3) com gradient SEMPRE visível no topo, independente do
              estado interno (com/sem voz selecionada). Os cards filhos perdem
              border/borderRadius pra não duplicar molduras. */}
          <div style={{
            background: 'var(--dp-bg-card)',
            border: '1px solid var(--dp-border-subtle)',
            borderRadius: 'var(--dp-radius-md, 12px)',
            // auto (não hidden): se os blocos fixos (voz + avisos multivoz +
            // dropdowns + barra Gerar) não couberem, a coluna rola em vez de
            // cortar o editor/botão. Em altura normal não aparece scrollbar.
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            // Piso de 560px (ver coluna esquerda): tela baixa/zoom alto deixa a
            // página rolar em vez de esmagar o editor de texto até sumir.
            height: 'max(calc(100vh - 220px), 560px)',
            padding: '12px',
          }}>
            {/* Gradient top bar — SEMPRE visível, mesma estrutura das Cols 1 e 3 */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
              background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)',
              opacity: 0.6,
              borderRadius: 'var(--dp-radius-md, 12px) var(--dp-radius-md, 12px) 0 0',
              zIndex: 2,
              pointerEvents: 'none',
            }} />

            {/* ──── Selected voice card (sem moldura própria — herda do outer) ──── */}
            {selectedVoiceData ? (
              <div style={{
                background: 'transparent',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: 'var(--dp-radius-sm, 8px)',
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                position: 'relative',
                // overflow: 'visible' garante que o dropdown de idiomas não seja cortado
                overflow: 'visible',
                flexShrink: 0,
              }}>
                {/* Large avatar with play and pulsing ring */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <VoiceAvatar
                    voice={selectedVoiceData}
                    size={44}
                    isPreviewing={previewingVoiceId === selectedVoiceData.id}
                    onPlayClick={getPreviewUrl(selectedVoiceData) ? () => toggleVoicePreview(selectedVoiceData) : undefined}
                  />
                  <div className="ag-pulse-ring" style={{
                    position: 'absolute', inset: '-3px', borderRadius: '50%',
                    border: '2px solid rgba(13,174,255,0.25)',
                  }} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--dp-text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedVoiceData.name}</span>
                  </div>
                  {/* ITER FINAL: linha de meta completa (igual à lista lateral):
                      Gênero · 🇮🇳 Hindi · +15 · 11Labs */}
                  {(() => {
                    const headerGender = selectedVoiceData.gender === 'female' ? 'Feminino' : selectedVoiceData.gender === 'male' ? 'Masculino' : (selectedVoiceData.genero || '');
                    const headerFlagCode = getPrimaryFlagCode(selectedVoiceData);
                    const headerLangName = getPrimaryLanguageName(selectedVoiceData);
                    const headerOthers = (() => {
                      const info = selectedVoiceData.voice_info;
                      if (!info) return 0;
                      const uniq = uniqueLanguages(info.verified_languages);
                      const primaryLang = (info.language || '').toLowerCase();
                      const primaryAccent = (info.accent || '').toLowerCase();
                      return uniq.filter((e) => {
                        const l = (e.language || '').toLowerCase();
                        const a = (e.accent || '').toLowerCase();
                        return l !== primaryLang || a !== primaryAccent;
                      }).length;
                    })();
                    return (
                      <div style={{
                        fontSize: '12px',
                        color: 'var(--dp-text-secondary)',
                        marginTop: '3px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        flexWrap: 'wrap',
                      }}>
                        {headerGender && <span>{headerGender}</span>}
                        {headerFlagCode && headerFlagCode !== 'xx' && (
                          <>
                            {headerGender && <span style={{ opacity: 0.4 }}>·</span>}
                            <DarkTooltip content={getCountryName(headerFlagCode)} delay={300}>
                              <FlagIcon code={headerFlagCode} size="14px" rounded />
                            </DarkTooltip>
                            {headerLangName && <span>{headerLangName}</span>}
                          </>
                        )}
                        {headerOthers > 0 && (
                          <DarkTooltip
                            content={`${headerOthers} ${headerOthers === 1 ? 'idioma adicional' : 'idiomas adicionais'} — clique pra ver`}
                            delay={300}
                          >
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setHeaderLangAnchor(e.currentTarget.getBoundingClientRect());
                              }}
                              style={{
                                fontSize: '10px',
                                fontWeight: 600,
                                color: 'var(--dp-blue-500)',
                                background: 'rgba(13,174,255,0.10)',
                                padding: '1px 6px',
                                borderRadius: '6px',
                                lineHeight: 1.4,
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: 'var(--dp-font)',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.18)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.10)'; }}
                            >+{headerOthers}</button>
                          </DarkTooltip>
                        )}
                        {selectedVoiceData.provider && (
                          <>
                            <span style={{ opacity: 0.4 }}>·</span>
                            <span style={{ color: 'var(--dp-blue-500)', opacity: 0.8 }}>{selectedVoiceData.provider}</span>
                          </>
                        )}
                      </div>
                    );
                  })()}
                  {/* FASE 2.1: Dropdown de previews por idioma (estilo ElevenLabs).
                      Aparece só pra vozes com voice_info + verified_languages.
                      AJUSTE 2: margin-top 12px pra dar respiro do gênero/provider acima. */}
                  {selectedVoiceData.voice_info && Array.isArray(selectedVoiceData.voice_info.verified_languages) && selectedVoiceData.voice_info.verified_languages.length > 0 && (
                    <div style={{ marginTop: '12px', position: 'relative', zIndex: 5 }}>
                      <LanguagePreviewDropdown
                        voice={selectedVoiceData}
                        inline={true}
                        currentLanguageKey={selectedLanguageKey}
                        onPlayLanguage={handleSelectedLanguagePreview}
                      />
                    </div>
                  )}
                </div>
                {/* Popover read-only de idiomas do header (acionado pelo botão "+N") */}
                {headerLangAnchor && (
                  <LanguageListPopover
                    voice={selectedVoiceData}
                    anchorRect={headerLangAnchor}
                    onClose={() => setHeaderLangAnchor(null)}
                  />
                )}
              </div>
            ) : (
              <div style={{
                background: 'transparent',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: 'var(--dp-radius-sm, 8px)',
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexShrink: 0,
              }}>
                <span style={{ fontSize: '18px' }}>👈</span>
                <p style={{ fontSize: '13px', color: 'var(--dp-text-tertiary)', margin: 0 }}>
                  {t('audio.select_voice_hint')}
                </p>
              </div>
            )}

            {/* ──── Multivoz: callout informativo de cobrança (só com Multivoz selecionada) ──── */}
            {isMultivoiceMode && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '10px 14px',
                background: 'rgba(13,174,255,0.06)',
                border: '1px solid rgba(13,174,255,0.22)',
                borderRadius: '10px',
                fontFamily: 'var(--dp-font)',
              }}>
                <Info size={15} style={{ color: 'var(--dp-blue-500)', flexShrink: 0, marginTop: '1px' }} />
                <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--dp-text-primary)' }}>Cobrança por voz única:</strong>{' '}
                  cada voz diferente que você usar consome <strong style={{ color: 'var(--dp-blue-500)' }}>1 crédito de áudio</strong>.{' '}
                  Ex: 4 vozes diferentes na narração = 4 áudios descontados.
                </span>
              </div>
            )}

            {/* ──── Multivoz: callout de instruções de uso ──── */}
            {isMultivoiceMode && (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '10px 14px',
                background: 'rgba(13,174,255,0.06)',
                border: '1px solid rgba(13,174,255,0.22)',
                borderRadius: '10px',
                fontFamily: 'var(--dp-font)',
              }}>
                <BookOpen size={15} style={{ color: 'var(--dp-blue-500)', flexShrink: 0, marginTop: '1px' }} />
                <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', lineHeight: 1.5, flex: 1, minWidth: 0 }}>
                  <div>
                    <strong style={{ color: 'var(--dp-text-primary)' }}>Como usar:</strong>{' '}
                    coloque o slug da voz entre colchetes{' '}
                    <code style={{ background: 'rgba(13,174,255,0.12)', color: 'var(--dp-blue-500)', padding: '1px 5px', borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace' }}>[slug]</code>{' '}
                    antes de cada fala. Ao detectar um novo{' '}
                    <code style={{ background: 'rgba(13,174,255,0.12)', color: 'var(--dp-blue-500)', padding: '1px 5px', borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace' }}>[slug]</code>, a voz muda automaticamente.
                  </div>
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 10px',
                    background: 'rgba(0,0,0,0.25)',
                    border: '1px solid rgba(13,174,255,0.18)',
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    color: 'var(--dp-text-primary)',
                    lineHeight: 1.55,
                    whiteSpace: 'pre-wrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    <span style={{ color: 'var(--dp-blue-500)', fontWeight: 600 }}>[mark]</span>
                    {' Olá pessoal, tudo bem? '}
                    <span style={{ color: 'var(--dp-blue-500)', fontWeight: 600 }}>[aami]</span>
                    {' Tudo ótimo, e você? '}
                    <span style={{ color: 'var(--dp-blue-500)', fontWeight: 600 }}>[mark]</span>
                    {' Vamos começar então.'}
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>
                    Pra descobrir os slugs disponíveis, use o botão <strong style={{ color: 'var(--dp-text-secondary)' }}>copiar slug</strong> embaixo do coração de cada voz na biblioteca.
                  </div>
                </div>
              </div>
            )}

            {/* ──── Channel / Script dropdowns ──── */}
            {canais.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '5px' }}>
                    {t('audio.channel')}
                  </div>
                  <CustomDropdown
                    value={selectedCanal}
                    onChange={(val) => setSelectedCanal(val)}
                    placeholder={t('audio.all_channels')}
                    options={[
                      { value: '', label: t('audio.all_channels') },
                      ...canais.map(canal => ({ value: canal.id, label: (canal.nome || canal.nome_canal || 'Canal sem nome') + (canal._is_shared ? ' (Editor)' : '') }))
                    ]}
                  />
                </div>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {t('audio.script')}
                    {roteirosDisponiveis.length > 0 && (
                      <span style={{ color: 'var(--dp-green-500, #34D399)', fontSize: '9px', fontWeight: 600 }}>
                        {roteirosDisponiveis.length} {t('audio.available')}
                      </span>
                    )}
                  </div>
                  <CustomDropdown
                    value={selectedRoteiro}
                    onChange={(val) => handleSelecionarRoteiro(val)}
                    disabled={roteirosDisponiveis.length === 0}
                    placeholder={roteirosDisponiveis.length === 0 ? t('audio.no_pending_scripts') : t('audio.select_script')}
                    options={[
                      { value: '', label: roteirosDisponiveis.length === 0 ? t('audio.no_pending_scripts') : t('audio.select_script') },
                      ...roteirosDisponiveis.map(roteiro => ({
                        value: roteiro.video_id,
                        label: `${roteiro.titulo || `Vídeo #${roteiro.numero_video || 'S/N'}`}${!selectedCanal && roteiro.canal_nome ? ` (${roteiro.canal_nome})` : ''}`
                      }))
                    ]}
                  />
                </div>
              </div>
            )}

            {/* ──── Config source indicator ──── */}
            {configSource && configSource !== 'system' && (
              <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', fontStyle: 'italic', marginTop: '-4px' }}>
                {configSource === 'channel'
                  ? `⚙ Configuração do canal '${configChannelName}' aplicada`
                  : '⚙ Suas preferências aplicadas'}
              </div>
            )}

            {/* ──── Text editor ──── */}
            <div
              className="ag-text-editor"
              style={{
                flex: 1,
                // Piso: o editor era o ÚNICO filho compressível da coluna e
                // colapsava até 0px em tela baixa. Nunca menor que isso.
                minHeight: '180px',
                background: 'var(--dp-bg-card)',
                border: `1px solid ${isDragging ? 'var(--dp-blue-500)' : 'var(--dp-border-subtle)'}`,
                borderRadius: 'var(--dp-radius-md, 12px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s',
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Toolbar */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 14px',
                background: 'rgba(255,255,255,0.015)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '5px 10px', borderRadius: '9999px',
                      border: '1px dashed var(--dp-border-medium)', background: 'transparent',
                      color: 'var(--dp-text-secondary)', fontSize: '11px', fontFamily: 'var(--dp-font)',
                      cursor: 'pointer', transition: 'all 0.3s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; e.currentTarget.style.borderStyle = 'solid'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; e.currentTarget.style.borderStyle = 'dashed'; }}
                  >
                    <Upload size={11} /> {t('audio.file_upload')}
                  </button>
                  <input ref={fileInputRef} type="file" accept=".txt,.docx" multiple onChange={(e) => {
                    const files = Array.from(e.target.files);
                    if (files.length === 1) handleSingleFileUpload(files[0]);
                    else if (files.length > 1) handleMultipleFilesUpload(files);
                  }} style={{ display: 'none' }} />

                  {/* Botão de Pausa — insere <break time="Xs"/> no cursor (durações no dropdown) */}
                  <div style={{ position: 'relative' }}>
                    <DarkTooltip
                      position="bottom"
                      delay={250}
                      maxWidth={250}
                      content={(
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: 600, color: 'var(--dp-text-primary)', marginBottom: '3px' }}>{t('audio.pause_label')}</div>
                          <div style={{ color: 'var(--dp-text-secondary)', marginBottom: '6px' }}>{t('audio.pause_tip')}</div>
                          <div style={{ display: 'inline-block', background: 'rgba(13,174,255,0.10)', border: '1px solid rgba(13,174,255,0.22)', borderRadius: '5px', padding: '2px 7px', fontFamily: 'monospace', fontSize: '10.5px', color: 'var(--dp-blue-500)', marginBottom: '6px' }}>&lt;break time="1.5s"/&gt;</div>
                          <div style={{ color: 'var(--dp-text-tertiary)', fontSize: '10px' }}>{t('audio.pause_hint')}</div>
                        </div>
                      )}
                    >
                    <button
                      type="button"
                      onClick={() => setPauseMenuOpen(v => !v)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '5px',
                        padding: '5px 10px', borderRadius: '9999px',
                        border: `1px solid ${pauseMenuOpen ? 'var(--dp-blue-500)' : 'var(--dp-border-medium)'}`,
                        background: pauseMenuOpen ? 'rgba(13,174,255,0.08)' : 'transparent',
                        color: pauseMenuOpen ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
                        fontSize: '11px', fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => { if (!pauseMenuOpen) { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.color = 'var(--dp-blue-500)'; } }}
                      onMouseLeave={(e) => { if (!pauseMenuOpen) { e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; } }}
                    >
                      <Pause size={11} /> {t('audio.pause_label')}
                      <ChevronDown size={11} style={{ opacity: 0.6, transform: pauseMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                    </button>
                    </DarkTooltip>
                    {pauseMenuOpen && (
                      <>
                        <div onClick={() => setPauseMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 60 }} />
                        <div style={{
                          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 61,
                          background: 'var(--dp-bg-elevated, #303030)', border: '1px solid var(--dp-border-medium)',
                          borderRadius: '10px', padding: '8px', minWidth: '184px',
                          boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
                        }}>
                          <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '2px 6px 7px' }}>
                            {t('audio.pause_duration')}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px' }}>
                            {['0.5', '1', '1.5', '2', '3'].map((s) => (
                              <button
                                key={s} type="button" onClick={() => insertBreakTag(s)}
                                style={{
                                  padding: '8px 4px', borderRadius: '8px', border: '1px solid var(--dp-border-subtle)',
                                  background: 'transparent', color: 'var(--dp-text-primary)', fontSize: '12px', fontWeight: 600,
                                  fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.15s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.background = 'rgba(13,174,255,0.08)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.background = 'transparent'; }}
                              >
                                {s}s
                              </button>
                            ))}
                          </div>
                          <div style={{ fontSize: '10px', color: 'var(--dp-text-tertiary)', padding: '8px 6px 2px', lineHeight: 1.45 }}>
                            {t('audio.pause_hint')}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <span style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>
                    {text.length.toLocaleString('pt-BR')} {t('audio.characters')} · {wordCount.toLocaleString('pt-BR')} {t('audio.words')}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <span style={{ padding: '4px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 500, border: '1px solid rgba(52,211,153,0.15)', color: 'var(--dp-green-500, #34D399)', background: 'rgba(255,255,255,0.03)' }}>
                    {narrationNormal}
                  </span>
                  <span style={{ padding: '4px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 500, border: '1px solid rgba(251,191,36,0.15)', color: '#FBBF24', background: 'rgba(255,255,255,0.03)' }}>
                    {narrationSlow}
                  </span>
                  <span style={{ padding: '4px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 500, border: '1px solid rgba(251,146,60,0.15)', color: '#FB923C', background: 'rgba(255,255,255,0.03)' }}>
                    {narrationFast}
                  </span>
                </div>
              </div>

              {/* Multivoz: detector de vozes em tempo real (informativo, não bloqueia) */}
              {isMultivoiceMode && detectedVoicesInfo.length > 0 && (
                <div
                  style={{
                    background: 'var(--dp-bg-card, #1a1f2e)',
                    border: '1px solid var(--dp-border-subtle, #2D3748)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    marginBottom: '8px',
                    fontFamily: 'var(--dp-font)',
                    fontSize: '12px',
                    color: 'var(--dp-text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '6px',
                  }}
                >
                  <Mic size={12} style={{ color: 'var(--dp-blue-500)', flexShrink: 0 }} />
                  <span style={{ fontWeight: 600, color: 'var(--dp-text-primary)' }}>
                    Vozes detectadas ({detectedVoicesInfo.length}/6):
                  </span>
                  {detectedVoicesInfo.map((info, i) => (
                    <span
                      key={info.slug}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <span
                        style={{
                          color: info.valid ? '#34D399' : '#EF4444',
                          fontWeight: 500,
                        }}
                      >
                        {info.valid ? info.voice.name : `slug inválido: ${info.slug}`}
                      </span>
                      <DarkTooltip content={`Trocar [${info.slug}]`} delay={400}>
                        <button
                          type="button"
                          onClick={() => openSwap(info.slug)}
                          style={{
                            padding: '2px 7px',
                            borderRadius: '6px',
                            background: 'transparent',
                            border: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.08))',
                            color: 'var(--dp-text-tertiary)',
                            fontSize: '10px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            fontFamily: 'var(--dp-font)',
                            lineHeight: 1.2,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(13,174,255,0.10)';
                            e.currentTarget.style.borderColor = 'rgba(13,174,255,0.30)';
                            e.currentTarget.style.color = 'var(--dp-blue-500)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = 'var(--dp-border-subtle, rgba(255,255,255,0.08))';
                            e.currentTarget.style.color = 'var(--dp-text-tertiary)';
                          }}
                        >
                          Trocar
                        </button>
                      </DarkTooltip>
                      {i < detectedVoicesInfo.length - 1 && (
                        <span style={{ color: 'var(--dp-text-tertiary)', fontWeight: 400 }}>{', '}</span>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('audio.text_placeholder')}
                style={{
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  padding: '14px 18px',
                  background: 'transparent',
                  border: 'none',
                  resize: 'none',
                  color: 'var(--dp-text-primary)',
                  fontFamily: 'var(--dp-font)',
                  fontSize: '14px',
                  lineHeight: '1.7',
                  outline: 'none',
                }}
              />

              {/* Footer */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 14px', background: 'rgba(255,255,255,0.015)',
              }}>
                <span style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>
                  <span style={{ fontWeight: 500, color: 'var(--dp-text-secondary)' }}>{text.length.toLocaleString('pt-BR')}</span>
                  {' '}/{' '}
                  {usage.unlimited || usage.character_limit === -1 ? '∞' : (usage.character_limit || 80000).toLocaleString('pt-BR')}
                </span>
              </div>
            </div>

            {/* ──── Controls bar ──── */}
            <div style={{
              background: 'var(--dp-bg-card)',
              border: '1px solid var(--dp-border-subtle)',
              borderRadius: 'var(--dp-radius-md, 12px)',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexShrink: 0,
              marginTop: 'auto',
            }}>
              {/* Name input */}
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder={t('audio.audio_name_placeholder')}
                style={{
                  flex: 1, minWidth: '80px', padding: '8px 12px',
                  background: 'var(--dp-bg-input, var(--dp-bg-primary))',
                  border: '1px solid var(--dp-border-subtle)',
                  borderRadius: 'var(--dp-radius-sm, 8px)',
                  color: 'var(--dp-text-primary)',
                  fontFamily: 'var(--dp-font)', fontSize: '13px',
                  outline: 'none', transition: 'all 0.3s',
                }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500)'; e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.25)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; e.target.style.boxShadow = 'none'; }}
              />

              {/* Botão "Configurações" removido: as configs agora vivem na aba
                  direita (Configurações | Histórico), sempre visíveis. O popover
                  abaixo é legado e nunca renderiza (showSettingsPopover fica false). */}
              <div style={{ position: 'relative', display: 'none' }}>
                {/* Unified settings popover (legado, desativado) */}
                {showSettingsPopover && (
                  <div
                    ref={settingsPopupRef}
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 10px)',
                      right: 0,
                      width: '300px',
                      padding: '0',
                      background: isDarkMode ? 'rgba(24, 24, 27, 0.92)' : 'rgba(250, 250, 250, 0.95)',
                      backdropFilter: 'blur(24px)',
                      WebkitBackdropFilter: 'blur(24px)',
                      border: '1px solid var(--dp-border-medium)',
                      borderRadius: 'var(--dp-radius-md, 12px)',
                      boxShadow: '0 16px 48px rgba(0,0,0,0.5), 0 0 1px rgba(13,174,255,0.2)',
                      zIndex: 200,
                      animation: 'agPopupIn 0.3s cubic-bezier(0.4,0,0.2,1)',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header */}
                    <div style={{ padding: '14px 16px 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Settings size={14} style={{ color: 'var(--dp-blue-500)' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>
                        {t('audio.settings') || 'Configurações'}
                      </span>
                    </div>

                    {/* ─── ÁUDIO Section ─── */}
                    <div style={{ padding: '0 16px 14px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
                        {t('audio.audio_section') || 'Áudio'}
                      </div>

                      {/* Speed slider */}
                      <div style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>{t('audio.speed')}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)' }}>{speed.toFixed(1)}x</span>
                        </div>
                        <input type="range" min="0.5" max="1.5" step="0.1" value={speed} onChange={(e) => setSpeed(parseFloat(e.target.value))} className="ag-slider" style={{ width: '100%' }} />
                      </div>

                      {/* Volume slider */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>{t('audio.volume')}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)' }}>{volume.toFixed(1)}x</span>
                        </div>
                        <input type="range" min="0.5" max="2" step="0.1" value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} className="ag-slider" style={{ width: '100%' }} />
                      </div>
                    </div>

                    {/* Separator */}
                    <div style={{ height: '1px', background: 'var(--dp-border-subtle)', margin: '0 16px' }} />

                    {/* ─── LEGENDA Section ─── */}
                    <div style={{ padding: '14px 16px 16px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
                        {t('audio.subtitle_section') || 'Legenda'}
                      </div>

                      {/* SRT Toggle */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>{t('audio.generate_srt') || 'Gerar Legenda (SRT)'}</span>
                        <button
                          onClick={() => setIncludeSrt(!includeSrt)}
                          style={{
                            width: '34px', height: '19px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                            position: 'relative', flexShrink: 0,
                            background: includeSrt ? 'var(--dp-blue-500)' : 'var(--dp-border-medium)',
                            transition: 'background 0.2s',
                          }}
                        >
                          <div style={{
                            position: 'absolute', width: '15px', height: '15px', borderRadius: '50%',
                            background: 'white', top: '2px',
                            right: includeSrt ? '2px' : 'auto',
                            left: includeSrt ? 'auto' : '2px',
                            transition: 'all 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                          }} />
                        </button>
                      </div>

                      {/* Subtitle options (only if SRT enabled) */}
                      {includeSrt && (
                        <>
                          {/* SRT 1 — Legenda (legenda.srt): MÁX. de palavras/linha, com pausas naturais */}
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--dp-text-secondary)' }}>
                                {t('audio.subtitle_legenda')}
                                <DarkTooltip content={t('audio.tip_legenda')} maxWidth={260}>
                                  <Info size={12} style={{ color: 'var(--dp-text-tertiary)', cursor: 'help' }} />
                                </DarkTooltip>
                              </span>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)' }}>{t('audio.max_label')} {subtitleWords}</span>
                            </div>
                            <input type="range" min="1" max="15" step="1" value={subtitleWords} onChange={(e) => setSubtitleWords(parseInt(e.target.value))} className="ag-slider" style={{ width: '100%' }} />
                          </div>

                          {/* SRT 2 — Por palavra (srtsyncpalavra.srt): total FIXO de palavras/linha */}
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--dp-text-secondary)' }}>
                                {t('audio.subtitle_palavra')}
                                <DarkTooltip content={t('audio.tip_palavra')} maxWidth={260}>
                                  <Info size={12} style={{ color: 'var(--dp-text-tertiary)', cursor: 'help' }} />
                                </DarkTooltip>
                              </span>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)' }}>{subtitleVeoWords}</span>
                            </div>
                            <input type="range" min="1" max="15" step="1" value={subtitleVeoWords} onChange={(e) => setSubtitleVeoWords(parseInt(e.target.value))} className="ag-slider" style={{ width: '100%' }} />
                          </div>

                          {/* SRT 3 — Por tempo (srtsynctempo.srt): duração ALEATÓRIA por bloco, range 1-60s (padrão 5-8).
                              Duas pontas; max sempre >= min (clamp no onChange). */}
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--dp-text-secondary)' }}>
                                {t('audio.subtitle_tempo')}
                                <DarkTooltip content={t('audio.tip_tempo')} maxWidth={260}>
                                  <Info size={12} style={{ color: 'var(--dp-text-tertiary)', cursor: 'help' }} />
                                </DarkTooltip>
                              </span>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)' }}>{subtitleVeoMin}–{subtitleVeoMax}s</span>
                            </div>
                            <div style={{ position: 'relative', height: '16px', display: 'flex', alignItems: 'center' }}>
                              {/* trilho base */}
                              <div style={{ position: 'absolute', left: 0, right: 0, height: '3px', background: 'var(--dp-bg-input, #252525)', borderRadius: '2px' }} />
                              {/* segmento selecionado (min→max) */}
                              <div style={{
                                position: 'absolute', height: '3px', borderRadius: '2px',
                                background: 'linear-gradient(90deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))',
                                left: `${((subtitleVeoMin - 1) / 59) * 100}%`,
                                right: `${100 - ((subtitleVeoMax - 1) / 59) * 100}%`,
                              }} />
                              <input
                                type="range" min="1" max="60" step="1" value={subtitleVeoMin}
                                onChange={(e) => setSubtitleVeoMin(Math.min(parseInt(e.target.value), subtitleVeoMax))}
                                className="ag-range-dual" style={{ zIndex: 3 }}
                                aria-label={`${t('audio.veo_duration')} (min)`}
                              />
                              <input
                                type="range" min="1" max="60" step="1" value={subtitleVeoMax}
                                onChange={(e) => setSubtitleVeoMax(Math.max(parseInt(e.target.value), subtitleVeoMin))}
                                className="ag-range-dual" style={{ zIndex: 4 }}
                                aria-label={`${t('audio.veo_duration')} (max)`}
                              />
                            </div>
                          </div>

                          {/* Format buttons */}
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', marginBottom: '6px' }}>{t('audio.format')}</div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              {[
                                { id: 'normal', label: 'Aa' },
                                { id: 'upper', label: 'AA' },
                                { id: 'lower', label: 'aa' },
                              ].map(opt => (
                                <button
                                  key={opt.id}
                                  onClick={() => setSubtitleCase(opt.id)}
                                  style={{
                                    flex: 1, padding: '6px 0', textAlign: 'center', borderRadius: '9999px',
                                    border: subtitleCase === opt.id ? '1px solid var(--dp-blue-500)' : '1px solid var(--dp-border-subtle)',
                                    background: subtitleCase === opt.id ? 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))' : 'transparent',
                                    color: subtitleCase === opt.id ? 'white' : 'var(--dp-text-secondary)',
                                    fontSize: '12px', fontWeight: 600, fontFamily: 'var(--dp-font)', cursor: 'pointer',
                                    transition: 'all 0.3s',
                                    boxShadow: subtitleCase === opt.id ? '0 0 12px rgba(13,174,255,0.25)' : 'none',
                                  }}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* ─── FOOTER: Reset + Save as default ─── */}
                    <div style={{ height: '1px', background: 'var(--dp-border-subtle)', margin: '0 16px' }} />
                    <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <button
                        onClick={() => {
                          setSpeed(1.0);
                          setVolume(1.0);
                          setIncludeSrt(true);
                          setSubtitleWords(5);
                          setSubtitleCase('normal');
                          setSubtitleVeoWords(5);
                          setSubtitleVeoMin(7);
                          setSubtitleVeoMax(8);
                          setConfigSource('system');
                        }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '11px', color: 'var(--dp-text-tertiary)',
                          fontFamily: 'var(--dp-font)', padding: 0,
                          textDecoration: 'underline', textUnderlineOffset: '2px',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
                      >
                        Resetar
                      </button>
                      <button
                        onClick={() => { saveAsDefault(); setShowSettingsPopover(false); }}
                        disabled={savingPrefs}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '5px',
                          padding: '5px 12px', borderRadius: '9999px',
                          background: savingPrefs ? 'var(--dp-border-medium)' : 'linear-gradient(135deg, var(--dp-blue-500), var(--dp-blue-600, #0B8FDB))',
                          border: 'none', color: 'white', fontSize: '11px', fontWeight: 600,
                          fontFamily: 'var(--dp-font)', cursor: savingPrefs ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s', opacity: savingPrefs ? 0.6 : 1,
                          boxShadow: '0 0 12px rgba(13,174,255,0.2)',
                        }}
                        onMouseEnter={(e) => { if (!savingPrefs) e.currentTarget.style.boxShadow = '0 0 18px rgba(13,174,255,0.35)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 0 12px rgba(13,174,255,0.2)'; }}
                      >
                        <Save size={11} />
                        {savingPrefs ? 'Salvando...' : 'Salvar como padrão'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Generate button — padrão premium .dp-btn-cta (gradiente da marca + seta) */}
              <button
                className="dp-btn-cta dp-btn-cta--sm"
                onClick={handleGenerate}
                disabled={!text.trim() || !selectedVoice}
              >
                <Mic size={14} /> {t('audio.generate')}
                <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* ═══════════ RIGHT: RESULTS PANEL ═══════════ */}
          <div style={{
            background: 'var(--dp-bg-card)',
            border: '1px solid var(--dp-border-subtle)',
            borderRadius: 'var(--dp-radius-md, 12px)',
            display: 'flex',
            flexDirection: 'column',
            // Mesmo piso das outras colunas (tela baixa/zoom alto → página rola).
            height: wideResults ? 'auto' : 'max(calc(100vh - 220px), 560px)',
            overflow: wideResults ? 'visible' : 'hidden',
            animation: 'agFadeInUp 0.35s ease 0.08s both',
            position: 'relative',
          }}>
            {/* Gradient top bar — mesma faixa decorativa das outras colunas (unifica TTS) */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
              background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)',
              opacity: 0.6,
              borderRadius: 'var(--dp-radius-md, 12px) var(--dp-radius-md, 12px) 0 0',
              zIndex: 1,
            }} />
          <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', flex: 1, overflow: wideResults ? 'visible' : 'hidden', minHeight: 0 }}>
          {/* Abas: Configurações | Histórico. No layout 2-col (painel embaixo), as
              abas somem e config + resultados aparecem EMPILHADOS (config compacta). */}
          {!wideResults && (
          <div className="dp-tabs" style={{ marginBottom: '12px', flexShrink: 0 }}>
            <button type="button" className={`dp-tab${rightTab === 'config' ? ' dp-tab--active' : ''}`} onClick={() => setRightTab('config')}>
              {t('audio.settings') || 'Configurações'}
            </button>
            <button type="button" className={`dp-tab${rightTab === 'history' ? ' dp-tab--active' : ''}`} onClick={() => setRightTab('history')}>
              {t('audio.results') || 'Histórico'}{queue.length ? ` (${queue.length})` : ''}
            </button>
          </div>
          )}

          {/* No layout 2-col: botão que abre/fecha a configuração (colapsada por padrão). */}
          {wideResults && (
            <button
              type="button"
              onClick={() => setCfgOpen((o) => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                padding: '9px 12px', marginBottom: cfgOpen ? '12px' : '14px', flexShrink: 0,
                background: cfgOpen ? 'rgba(13,174,255,0.06)' : 'var(--dp-bg-card)',
                border: '1px solid var(--dp-border-subtle)', borderRadius: '10px',
                cursor: 'pointer', color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)',
                fontSize: '13px', fontWeight: 600, transition: 'background 0.15s, border-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.30)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
            >
              <Settings size={15} style={{ color: 'var(--dp-blue-500)', flexShrink: 0 }} />
              <span>{t('audio.settings') || 'Configurações'}</span>
              <span style={{ flex: 1 }} />
              <ChevronDown size={16} style={{ color: 'var(--dp-text-tertiary)', transition: 'transform 0.2s', transform: cfgOpen ? 'rotate(180deg)' : 'none' }} />
            </button>
          )}

          {((wideResults && cfgOpen) || (!wideResults && rightTab === 'config')) && settingsPanel}

          {wideResults && cfgOpen && <div style={{ height: '1px', background: 'var(--dp-border-subtle)', margin: '6px 2px 14px' }} />}

          {(wideResults || rightTab === 'history') && (
          <>
          {/* Results header — ações condicionais NA MESMA LINHA (nunca quebra).
              selectedCount === 0 → [Selecionar] [🗑 Limpar concluídos]
              selectedCount  > 0 → [Limpar seleção] [ZIP (N)] [🗑 Excluir selecionados] */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', gap: '8px', flexShrink: 0 }}>
            {/* Título — DESTAQUE visual (14px / 600). Contador e badges discretos. */}
            <div style={{ fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--dp-text-primary)', minWidth: 0 }}>
              <FileAudio size={15} style={{ color: 'var(--dp-blue-500)', flexShrink: 0 }} />
              <span style={{ whiteSpace: 'nowrap' }}>{t('audio.results')}</span>
              <span style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', fontWeight: 400, flexShrink: 0 }}>({queue.length})</span>
              {pendingCount > 0 && (
                <span style={{
                  padding: '1px 6px', borderRadius: '9999px', fontSize: '9px', fontWeight: 600,
                  background: 'rgba(251,191,36,0.10)', color: '#FBBF24', flexShrink: 0,
                }}>{pendingCount}</span>
              )}
            </div>
            {/* Ações — visualmente SUBORDINADAS ao título (11px, borderRadius:5, discreto) */}
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexShrink: 0 }}>
              {selectedItems.size === 0 ? (
                /* ─── Modo padrão: Selecionar + Limpar concluídos ─── */
                <>
                  {completedCount > 0 && (
                    <DarkTooltip content="Selecionar todos os áudios concluídos" delay={400}>
                      <button onClick={toggleSelectAll} style={{
                        padding: '4px 8px', borderRadius: '5px',
                        border: '1px solid var(--dp-border-subtle)', background: 'transparent',
                        color: 'var(--dp-text-secondary)', fontSize: '11px', fontWeight: 500,
                        fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.25s',
                        display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                      }}>
                        <Square size={11} />
                        {t('audio.select_all') || 'Selecionar'}
                      </button>
                    </DarkTooltip>
                  )}
                  {completedCount > 0 && (
                    <DarkTooltip content="Limpar todos os áudios concluídos" delay={400}>
                      <button
                        onClick={clearCompleted}
                        style={TRASH_ICON_BTN}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(239,68,68,0.15)';
                          e.currentTarget.style.color = '#EF4444';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'var(--dp-text-tertiary)';
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </DarkTooltip>
                  )}
                </>
              ) : (
                /* ─── Modo seleção: Cancelar seleção + ZIP + Excluir selecionados ─── */
                <>
                  <DarkTooltip content="Cancelar seleção" delay={400}>
                    <button onClick={clearSelection} style={{
                      padding: '4px 8px', borderRadius: '5px',
                      border: '1px solid rgba(13,174,255,0.30)', background: 'rgba(13,174,255,0.08)',
                      color: 'var(--dp-blue-500)', fontSize: '11px', fontWeight: 500,
                      fontFamily: 'var(--dp-font)', cursor: 'pointer', transition: 'all 0.25s',
                      display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                    }}>
                      <X size={11} />
                      Limpar
                    </button>
                  </DarkTooltip>
                  <DarkTooltip content={`Baixar ZIP com ${selectedItems.size} ${selectedItems.size === 1 ? 'áudio' : 'áudios'}`} delay={400}>
                    <button onClick={downloadSelectedAsZip} disabled={isDownloadingZip} style={{
                      padding: '4px 8px', borderRadius: '5px',
                      border: '1px solid rgba(13,174,255,0.25)', background: 'rgba(13,174,255,0.10)',
                      color: '#0DAEFF', fontSize: '11px', fontWeight: 500, fontFamily: 'var(--dp-font)',
                      cursor: isDownloadingZip ? 'not-allowed' : 'pointer', transition: 'all 0.25s',
                      display: 'flex', alignItems: 'center', gap: '4px',
                    }}>
                      {isDownloadingZip
                        ? <><Loader2 size={11} className="animate-spin" /> {zipProgress.current}/{zipProgress.total}</>
                        : <><Package size={11} /> ZIP ({selectedItems.size})</>}
                    </button>
                  </DarkTooltip>
                  <DarkTooltip content={`Excluir ${selectedItems.size} ${selectedItems.size === 1 ? 'áudio selecionado' : 'áudios selecionados'}`} delay={400}>
                    <button
                      onClick={deleteSelected}
                      style={TRASH_ICON_BTN}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239,68,68,0.15)';
                        e.currentTarget.style.color = '#EF4444';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'var(--dp-text-tertiary)';
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </DarkTooltip>
                </>
              )}
            </div>
          </div>

          {/* Results cards — layout vertical premium */}
          <div className="ag-scrollbar ag-results-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {queue.length === 0 ? (
              /* Empty state premium */
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
                textAlign: 'center',
                opacity: 0.7,
              }}>
                <div style={{
                  width: 56, height: 56,
                  borderRadius: '50%',
                  background: 'rgba(13, 174, 255, 0.08)',
                  border: '1px solid rgba(13, 174, 255, 0.20)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 16,
                }}>
                  <Mic2 size={24} color="#0DAEFF" />
                </div>
                <div style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--dp-text-primary)',
                  marginBottom: 4,
                }}>
                  Nenhum áudio ainda
                </div>
                <div style={{
                  fontSize: 12,
                  color: 'var(--dp-text-secondary)',
                  lineHeight: 1.5,
                }}>
                  Digite seu texto à esquerda<br />e clique em <strong>Gerar Áudio</strong>
                </div>
              </div>
            ) : (
              queue
                .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                .map((item) => {
                  const isExpired = item.status === 'expired';
                  const isCompleted = item.status === 'completed';
                  const isProcessing = item.status === 'processing';
                  const isError = item.status === 'error';
                  const isItemPlaying = playingItemId === item.id && isPlaying;
                  const isSelected = selectedItems.has(item.id);
                  const isExpiring = isCompleted && typeof item.expiresInHours === 'number' && item.expiresInHours < 6;
                  const canPlay = isCompleted && !!item.audioUrl;

                  // Resolve voice obj pra mostrar bandeira do idioma na linha da voz.
                  // Prioriza item.voiceData (salvo no momento da criação via handleGenerate)
                  // e faz fallback pra lookup em voices state (items do history não trazem).
                  const voiceForFlag = item.voiceData || voices.find(v => v.id === item.voiceId) || null;
                  const primaryFlagCode = voiceForFlag ? getPrimaryFlagCode(voiceForFlag) : null;

                  // Estilos derivados do estado
                  const borderLeft = isItemPlaying ? '3px solid #0DAEFF' : '1px solid var(--dp-border-subtle)';
                  const defaultBg = isItemPlaying
                    ? 'rgba(13,174,255,0.05)'
                    : (isSelected ? 'rgba(13,174,255,0.08)' : 'var(--dp-bg-card)');
                  const defaultBorderColor = isSelected ? 'rgba(13,174,255,0.30)' : 'var(--dp-border-subtle)';

                  /* ═══ LAYOUT 2-COL: cada resultado em UMA LINHA (avatar + texto +
                     meta à esquerda · status + ações à direita). Compacto, estilo
                     player. O card detalhado (multi-linha) continua no layout 3-col. */
                  if (wideResults) {
                    const mainText = item.fullText || item.text || item.fileName || '—';
                    const seed = item.voiceName || mainText || '?';
                    let _h = 0; for (const ch of String(seed)) _h = (_h * 31 + ch.charCodeAt(0)) % 360;
                    const avBg = `linear-gradient(135deg, hsl(${_h},68%,56%), hsl(${(_h + 38) % 360},66%,46%))`;
                    const dispTotal = isItemPlaying ? (playerDuration || item.duration || 0) : (item.duration || 0);
                    const dispCur = isItemPlaying ? playerCurrentTime : 0;
                    const pct = dispTotal > 0 ? Math.min(100, (dispCur / dispTotal) * 100) : 0;
                    const srtOpts = isCompleted ? [
                      item.srt && { key: 'legenda', tip: t('audio.tip_legenda'), label: t('audio.dl_legenda'), icon: <FileText size={11} />, run: () => downloadSrt(item) },
                      item.srtPalavra && { key: 'palavra', tip: t('audio.tip_palavra'), label: t('audio.dl_palavra'), icon: <Type size={11} />, run: () => downloadSrt({ ...item, srt: item.srtPalavra, _suffix: 'palavra' }) },
                      item.srtTempo && { key: 'tempo', tip: t('audio.tip_tempo'), label: t('audio.dl_tempo'), icon: <Clock size={11} />, run: () => downloadSrt({ ...item, srt: item.srtTempo, _suffix: 'tempo' }) },
                      item.srtTempo && { key: 'editar', tip: t('audio.srt_edit_tip'), label: t('audio.srt_edit_btn'), icon: <Pencil size={11} />, run: () => openSrtEditor(item) },
                    ].filter(Boolean) : [];
                    const statusEl = (() => {
                      const base = { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', padding: '2px 8px', borderRadius: '9999px', display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'var(--dp-font)' };
                      if (isItemPlaying) return <span className="status-active" style={{ ...base, background: 'rgba(13,174,255,0.15)', color: '#0DAEFF' }}>{t('audio.st_active', 'Ativo')}</span>;
                      if (isExpiring) return <span style={{ ...base, background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>{Math.round(item.expiresInHours)}h</span>;
                      if (isCompleted) return <span style={{ ...base, background: 'rgba(16,185,129,0.12)', color: '#10B981' }}><Check size={10} /> {t('audio.st_done', 'Concluído')}</span>;
                      if (isProcessing) return <span style={{ ...base, background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}><Loader2 size={9} className="animate-spin" /> {t('audio.st_processing', 'Gerando')}</span>;
                      if (isError && item.backendStatus === 'blocked') return <span title={`${item.errorMessage || t('audio.moderation_blocked')} · ${t('audio.moderation_review', 'Revise o roteiro e tente novamente.')}`} style={{ ...base, background: 'rgba(239,68,68,0.15)', color: 'var(--dp-danger, #EF4444)' }}><ShieldAlert size={14} /> {t('audio.st_blocked', 'Bloqueado')}</span>;
                      if (isError) return <button type="button" onClick={(e) => { e.stopPropagation(); setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'pending' } : q)); }} style={{ ...base, background: 'rgba(239,68,68,0.15)', color: '#EF4444', border: 'none', cursor: 'pointer' }}>{t('audio.st_error', 'Erro')}</button>;
                      if (isExpired) return <span style={{ ...base, background: 'rgba(107,114,128,0.15)', color: 'var(--dp-text-tertiary)' }}>{t('audio.st_expired', 'Expirado')}</span>;
                      return null;
                    })();
                    return (
                      <div
                        key={item.id}
                        onClick={(e) => { if (e.target.closest('button')) return; if (item.fullText || item.text) openTextModal(item); }}
                        onMouseEnter={(e) => { if (isExpired || isItemPlaying || isSelected) return; e.currentTarget.style.borderColor = 'rgba(13,174,255,0.20)'; e.currentTarget.style.background = 'rgba(13,174,255,0.04)'; }}
                        onMouseLeave={(e) => { if (isItemPlaying || isSelected) return; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.background = defaultBg; }}
                        style={{
                          position: 'relative', display: 'flex', alignItems: 'center', gap: '11px',
                          background: defaultBg, border: `1px solid ${defaultBorderColor}`,
                          borderLeft: isItemPlaying ? '3px solid #0DAEFF' : `1px solid ${defaultBorderColor}`,
                          borderRadius: '10px', padding: '9px 14px', overflow: 'hidden',
                          opacity: isExpired ? 0.5 : 1, cursor: (item.fullText || item.text) ? 'pointer' : 'default',
                          minWidth: 0, width: '100%', boxSizing: 'border-box', transition: 'all 0.2s ease',
                          animation: !item.fromHistory ? 'agSlideIn 0.3s ease-out' : 'none',
                        }}
                      >
                        {/* seleção (só concluídos) */}
                        {canPlay ? (
                          <button onClick={(e) => { e.stopPropagation(); toggleSelectItem(item.id); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            {isSelected ? <CheckSquare size={15} style={{ color: 'var(--dp-blue-500)' }} /> : <Square size={15} style={{ color: 'var(--dp-text-tertiary)' }} />}
                          </button>
                        ) : <span style={{ width: 15, flexShrink: 0 }} />}

                        {/* avatar */}
                        <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: avBg, color: '#fff', fontWeight: 800, fontSize: 13, textTransform: 'uppercase' }}>
                          {(item.voiceName || mainText || '?').trim().charAt(0)}
                        </div>

                        {/* texto (linha 1) + meta (linha 2) */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.35 }}>
                            {mainText}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px', minWidth: 0 }}>
                            {primaryFlagCode && <span style={{ flexShrink: 0, display: 'inline-flex' }}><FlagIcon code={primaryFlagCode} size="12px" rounded /></span>}
                            <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--dp-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '40%' }}>
                              {isCompleted ? (item.voiceName || '—') : (isProcessing ? t('audio.st_processing', 'Gerando') + '…' : (isError ? (item.backendStatus === 'blocked' ? t('audio.st_blocked', 'Bloqueado') : t('audio.st_error', 'Erro')) : (item.voiceName || '—')))}
                            </span>
                            {item.createdAt && <><span style={{ color: 'var(--dp-text-tertiary)', opacity: 0.5 }}>·</span><span style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', opacity: 0.7, whiteSpace: 'nowrap' }}>{formatRelativeDate(item.createdAt)}</span></>}
                          </div>
                        </div>

                        {/* tempo (mono) — só quando tocando */}
                        {isItemPlaying && (
                          <span style={{ fontSize: '10px', fontFamily: "'JetBrains Mono', 'Courier New', monospace", color: 'var(--dp-text-secondary)', flexShrink: 0, letterSpacing: '0.3px' }}>
                            {formatTime(dispCur)} / {formatTime(dispTotal)}
                          </span>
                        )}

                        {/* ações à direita */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                          {statusEl}
                          {canPlay && (
                            <DarkTooltip content={isItemPlaying ? 'Pausar' : 'Tocar'} delay={400}>
                              <button onClick={(e) => { e.stopPropagation(); togglePlayItem(item); }} style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(13,174,255,0.10)', border: '1px solid rgba(13,174,255,0.30)', color: '#0DAEFF', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.20)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.10)'; }}>
                                {isItemPlaying ? <Pause size={13} /> : <Play size={13} style={{ marginLeft: 1 }} />}
                              </button>
                            </DarkTooltip>
                          )}
                          {canPlay && (
                            <DarkTooltip content={t('audio.dl_mp3', 'Baixar MP3')} delay={400}>
                              <button onClick={(e) => { e.stopPropagation(); downloadAudio(item); }} style={DOWNLOAD_BTN} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.18)'; e.currentTarget.style.borderColor = 'rgba(13,174,255,0.40)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.10)'; e.currentTarget.style.borderColor = 'rgba(13,174,255,0.22)'; }}>
                                <Download size={13} />
                              </button>
                            </DarkTooltip>
                          )}
                          {srtOpts.length > 0 && (
                            <div style={{ display: 'inline-flex', alignItems: 'stretch', border: '1px solid var(--dp-border-subtle)', borderRadius: '8px', overflow: 'hidden' }}>
                              {srtOpts.map((o, i) => (
                                <DarkTooltip key={o.key} content={o.tip} maxWidth={260} delay={300}>
                                  <button onClick={(e) => { e.stopPropagation(); o.run(); }} style={{ ...SEG_BTN, borderLeft: i > 0 ? '1px solid var(--dp-border-subtle)' : 'none' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.12)'; e.currentTarget.style.color = '#0DAEFF'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}>
                                    {o.icon}
                                  </button>
                                </DarkTooltip>
                              ))}
                            </div>
                          )}
                          {!isExpired && (
                            <DarkTooltip content="Excluir" delay={400}>
                              <button onClick={(e) => { e.stopPropagation(); removeFromQueue(item.id); }} style={{ ...TRASH_ICON_BTN, flexShrink: 0 }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#EF4444'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}>
                                <Trash2 size={14} />
                              </button>
                            </DarkTooltip>
                          )}
                        </div>

                        {/* preload metadata (igual ao card) */}
                        {canPlay && !item.duration && (
                          <audio preload="metadata" src={item.audioUrl} style={{ display: 'none' }} onLoadedMetadata={(e) => { const d = e.target.duration; if (d && !isNaN(d) && isFinite(d)) setQueue(prev => prev.map(q => q.id === item.id && !q.duration ? { ...q, duration: d } : q)); }} />
                        )}

                        {/* progresso = faixa fina na base, só quando tocando */}
                        {isItemPlaying && (
                          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '3px', background: 'var(--dp-border-medium)' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)', transition: 'width 0.15s linear' }} />
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div
                      key={item.id}
                      style={{
                        background: defaultBg,
                        border: `1px solid ${defaultBorderColor}`,
                        borderLeft,
                        borderRadius: '10px',
                        padding: '10px 12px',
                        transition: 'all 0.2s ease',
                        opacity: isExpired ? 0.5 : 1,
                        animation: !item.fromHistory ? 'agSlideIn 0.3s ease-out' : 'none',
                        cursor: (item.fullText || item.text) ? 'pointer' : 'default',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        // minWidth:0 + boxSizing:border-box garante que o card respeita
                        // a largura da coluna (270px) sem "inflar" com filename longo.
                        minWidth: 0,
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                      onClick={(e) => {
                        // Abrir modal de texto ao clicar no card (exceto botões)
                        if (e.target.closest('button')) return;
                        if (item.fullText || item.text) openTextModal(item);
                      }}
                      onMouseEnter={(e) => {
                        if (isExpired || isItemPlaying || isSelected) return;
                        e.currentTarget.style.borderColor = 'rgba(13,174,255,0.20)';
                        e.currentTarget.style.background = 'rgba(13,174,255,0.04)';
                      }}
                      onMouseLeave={(e) => {
                        if (isItemPlaying || isSelected) return;
                        e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                        e.currentTarget.style.background = 'var(--dp-bg-card)';
                      }}
                    >
                      {/* ─── LINHA 1: Controles (checkbox + play + progress + tempo) ─── */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                        {canPlay ? (
                          <DarkTooltip content={isSelected ? 'Desmarcar' : 'Selecionar'} delay={400}>
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleSelectItem(item.id); }}
                              style={{
                                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                                display: 'flex', alignItems: 'center', flexShrink: 0,
                              }}
                            >
                              {isSelected
                                ? <CheckSquare size={14} style={{ color: 'var(--dp-blue-500)' }} />
                                : <Square size={14} style={{ color: 'var(--dp-text-tertiary)' }} />}
                            </button>
                          </DarkTooltip>
                        ) : (
                          <span style={{ width: 14, height: 14, flexShrink: 0 }} />
                        )}

                        {/* Play 24×24 — ou placeholder com status icon */}
                        {canPlay ? (
                          <DarkTooltip content={isItemPlaying ? 'Pausar' : 'Tocar'} delay={400}>
                            <button
                              onClick={(e) => { e.stopPropagation(); togglePlayItem(item); }}
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                background: 'rgba(13,174,255,0.10)',
                                border: '1px solid rgba(13,174,255,0.30)',
                                color: '#0DAEFF',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'background 0.15s',
                                padding: 0,
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.20)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.10)'; }}
                            >
                              {isItemPlaying ? <Pause size={12} /> : <Play size={12} style={{ marginLeft: 1 }} />}
                            </button>
                          </DarkTooltip>
                        ) : (
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: 'transparent',
                            border: '1px solid var(--dp-border-subtle)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: 'var(--dp-text-tertiary)',
                          }}>
                            {isProcessing
                              ? <Loader2 size={12} className="animate-spin" />
                              : isError
                                ? <X size={12} style={{ color: '#EF4444' }} />
                                : <FileAudio size={12} />}
                          </div>
                        )}

                        {/* Progress bar — SEMPRE visível. Wrapper externo `<div flex:1>`
                            é CRÍTICO porque o DarkTooltip cria um <span inline-block>
                            que ignora flex:1 do filho. O wrapper externo garante que
                            a progress bar estica até o tempo (na borda direita). */}
                        {(() => {
                          // Tempos de display: usa state global do player se este item está tocando;
                          // senão usa item.duration (e current=0).
                          const displayTotal = isItemPlaying
                            ? (playerDuration || item.duration || 0)
                            : (item.duration || 0);
                          const displayCurrent = isItemPlaying ? playerCurrentTime : 0;
                          const pct = displayTotal > 0 ? Math.min(100, (displayCurrent / displayTotal) * 100) : 0;
                          return (
                            <div style={{ flex: 1, minWidth: 40, display: 'flex', alignItems: 'center' }}>
                              <DarkTooltip
                                content={canPlay ? (isItemPlaying ? `${formatTime(displayCurrent)} / ${formatTime(displayTotal)}` : 'Clique pra tocar') : ''}
                                delay={400}
                                block
                              >
                                <div
                                  onClick={canPlay ? (e) => { e.stopPropagation(); handleSeekOrPlay(e, item); } : undefined}
                                  style={{
                                    width: '100%',
                                    height: '4px',
                                    background: 'var(--dp-border-medium)',
                                    borderRadius: '2px',
                                    cursor: canPlay ? 'pointer' : 'default',
                                    overflow: 'hidden',
                                    position: 'relative',
                                  }}
                                >
                                  <div style={{
                                    height: '100%',
                                    width: `${pct}%`,
                                    background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)',
                                    borderRadius: '2px',
                                    transition: isItemPlaying ? 'width 0.15s linear' : 'width 0.2s ease',
                                  }} />
                                </div>
                              </DarkTooltip>
                            </div>
                          );
                        })()}

                        {/* Tempo "0:00 / 0:42" sempre visível */}
                        <span style={{
                          fontSize: '10px',
                          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                          color: 'var(--dp-text-secondary)',
                          flexShrink: 0,
                          minWidth: 60,
                          textAlign: 'right',
                          letterSpacing: '0.3px',
                        }}>
                          {(() => {
                            const total = isItemPlaying ? (playerDuration || item.duration || 0) : (item.duration || 0);
                            const cur = isItemPlaying ? playerCurrentTime : 0;
                            return `${formatTime(cur)} / ${formatTime(total)}`;
                          })()}
                        </span>

                        {/* Excluir — canto superior direito (fora da linha de downloads) */}
                        {!isExpired && (
                          <DarkTooltip content="Excluir" delay={400}>
                            <button
                              onClick={(e) => { e.stopPropagation(); removeFromQueue(item.id); }}
                              style={{ ...TRASH_ICON_BTN, flexShrink: 0 }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#EF4444'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </DarkTooltip>
                        )}
                      </div>

                      {/* ─── LINHA 2: TÍTULO (filename mono BOLD branco — destaque).
                          DarkTooltip com prop `block` usa <span display:block;width:100%>
                          pro wrapper, permitindo o filho com ellipsis usar 100% do espaço
                          do card antes de truncar (sem isso, inline-block achatava o filho). */}
                      <DarkTooltip content={item.fileName || ''} delay={500} block>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'var(--dp-text-primary)',
                          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                          letterSpacing: '-0.3px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block',
                          width: '100%',
                          minWidth: 0,
                          cursor: 'default',
                        }}>
                          {item.fileName || '—'}
                        </div>
                      </DarkTooltip>

                      {/* Preload metadata invisível pra popular item.duration ANTES do play.
                          Roda só pra cards completed sem duration conhecida. Carrega apenas
                          o header MP3 (~5KB) — barato e roda em background. */}
                      {canPlay && !item.duration && (
                        <audio
                          preload="metadata"
                          src={item.audioUrl}
                          style={{ display: 'none' }}
                          onLoadedMetadata={(e) => {
                            const dur = e.target.duration;
                            if (dur && !isNaN(dur) && isFinite(dur)) {
                              setQueue(prev => prev.map(q => q.id === item.id && !q.duration ? { ...q, duration: dur } : q));
                            }
                          }}
                        />
                      )}

                      {/* ─── LINHA 3: Bandeira do idioma + nome da voz (secundário, até 2 linhas) ─── */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', minWidth: 0 }}>
                        {primaryFlagCode && (
                          <span style={{ flexShrink: 0, paddingTop: '1px', display: 'inline-flex' }}>
                            <FlagIcon code={primaryFlagCode} size="12px" rounded />
                          </span>
                        )}
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 500,
                          color: 'var(--dp-text-secondary)',
                          lineHeight: 1.3,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'break-word',
                          flex: 1,
                          minWidth: 0,
                        }}>
                          {isCompleted
                            ? (item.voiceName || '—')
                            : (isProcessing ? 'Gerando…' : (isError ? 'Falhou' : (item.voiceName || '—')))}
                        </span>
                      </div>

                      {/* ─── LINHA 4: Data (esq) + Status badge (dir) ─── */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{
                          fontSize: '11px',
                          color: 'var(--dp-text-tertiary)',
                          opacity: 0.6,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {item.createdAt ? formatRelativeDate(item.createdAt) : ''}
                        </span>

                        {/* Status badge — 6 estados (playing/expiring/completed/processing/error/expired) */}
                        {(() => {
                          const base = {
                            fontSize: '10px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            fontFamily: 'var(--dp-font)',
                          };
                          if (isItemPlaying) {
                            return (
                              <DarkTooltip content="Tocando agora" delay={400}>
                                <span className="status-active" style={{
                                  ...base,
                                  background: 'rgba(13,174,255,0.15)',
                                  color: '#0DAEFF',
                                }}>
                                  Ativo
                                </span>
                              </DarkTooltip>
                            );
                          }
                          if (isExpiring) {
                            return (
                              <DarkTooltip content={`Expira em ${Math.round(item.expiresInHours)}h`} delay={400}>
                                <span style={{
                                  ...base,
                                  background: 'rgba(245,158,11,0.15)',
                                  color: '#F59E0B',
                                }}>
                                  {Math.round(item.expiresInHours)}h
                                </span>
                              </DarkTooltip>
                            );
                          }
                          if (isCompleted) {
                            return (
                              <DarkTooltip content="Concluído" delay={400}>
                                <span style={{
                                  ...base,
                                  color: '#10B981',
                                  opacity: 0.7,
                                  background: 'transparent',
                                  padding: '2px 4px',
                                }}>
                                  <Check size={10} />
                                </span>
                              </DarkTooltip>
                            );
                          }
                          if (isProcessing) {
                            return (
                              <DarkTooltip content="Gerando áudio…" delay={400}>
                                <span style={{
                                  ...base,
                                  background: 'rgba(245,158,11,0.15)',
                                  color: '#F59E0B',
                                }}>
                                  <Loader2 size={9} className="animate-spin" /> Gerando
                                </span>
                              </DarkTooltip>
                            );
                          }
                          if (isError && item.backendStatus === 'blocked') {
                            return (
                              <DarkTooltip content={`${item.errorMessage || t('audio.moderation_blocked')} · ${t('audio.moderation_review', 'Revise o roteiro e tente novamente.')}`} delay={400}>
                                <span style={{
                                  ...base,
                                  background: 'rgba(239,68,68,0.15)',
                                  color: 'var(--dp-danger, #EF4444)',
                                }}>
                                  <ShieldAlert size={14} /> {t('audio.st_blocked', 'Bloqueado')}
                                </span>
                              </DarkTooltip>
                            );
                          }
                          if (isError) {
                            return (
                              <DarkTooltip content="Clique pra tentar novamente" delay={400}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'pending' } : q));
                                  }}
                                  style={{
                                    ...base,
                                    background: 'rgba(239,68,68,0.15)',
                                    color: '#EF4444',
                                    border: 'none',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Erro
                                </button>
                              </DarkTooltip>
                            );
                          }
                          if (isExpired) {
                            return (
                              <DarkTooltip content="Áudio expirado — não disponível mais" delay={400}>
                                <span style={{
                                  ...base,
                                  background: 'rgba(107,114,128,0.15)',
                                  color: 'var(--dp-text-tertiary)',
                                }}>
                                  Expirado
                                </span>
                              </DarkTooltip>
                            );
                          }
                          return null;
                        })()}
                      </div>

                      {/* ─── LINHA 5: Downloads — 2 linhas ALINHADAS à esquerda:
                           (1) Baixar MP3 + Editar SRT Tempo VEO
                           (2) grupo segmentado de legendas SRT ─── */}
                      {(canPlay || (isCompleted && (item.srt || item.srtPalavra || item.srtTempo))) && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px', paddingTop: '4px' }}>
                          {(canPlay || (isCompleted && item.srtTempo)) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              {/* Baixar MP3 — download principal do áudio */}
                              {canPlay && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); downloadAudio(item); }}
                                  style={DOWNLOAD_BTN}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.18)'; e.currentTarget.style.borderColor = 'rgba(13,174,255,0.40)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.10)'; e.currentTarget.style.borderColor = 'rgba(13,174,255,0.22)'; }}
                                >
                                  <Download size={12} /> Baixar MP3
                                </button>
                              )}
                              {/* Editar SRT Tempo VEO — ao lado do Baixar MP3 */}
                              {isCompleted && item.srtTempo && (
                                <DarkTooltip content={t('audio.srt_edit_tip')} maxWidth={260} delay={300}>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openSrtEditor(item); }}
                                    style={{ ...SEG_BTN, border: '1px solid var(--dp-border-subtle)', borderRadius: '8px', color: '#0DAEFF' }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.12)'; e.currentTarget.style.borderColor = 'rgba(13,174,255,0.35)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
                                  >
                                    <Pencil size={11} /> {t('audio.srt_edit_btn')}
                                  </button>
                                </DarkTooltip>
                              )}
                            </div>
                          )}
                          {/* Legendas SRT — cada uma INDIVIDUAL, agrupadas num segmentado ("juntas") */}
                          {isCompleted && (item.srt || item.srtPalavra || item.srtTempo) && (() => {
                            const srtOpts = [
                              item.srt && { key: 'legenda', tip: t('audio.tip_legenda'), label: t('audio.dl_legenda'), icon: <FileText size={11} />, run: () => downloadSrt(item) },
                              item.srtPalavra && { key: 'palavra', tip: t('audio.tip_palavra'), label: t('audio.dl_palavra'), icon: <Type size={11} />, run: () => downloadSrt({ ...item, srt: item.srtPalavra, _suffix: 'palavra' }) },
                              item.srtTempo && { key: 'tempo', tip: t('audio.tip_tempo'), label: t('audio.dl_tempo'), icon: <Clock size={11} />, run: () => downloadSrt({ ...item, srt: item.srtTempo, _suffix: 'tempo' }) },
                            ].filter(Boolean);
                            return (
                              <div style={{ display: 'inline-flex', alignItems: 'stretch', border: '1px solid var(--dp-border-subtle)', borderRadius: '8px', overflow: 'hidden' }}>
                                {srtOpts.map((o, i) => (
                                  <DarkTooltip key={o.key} content={o.tip} maxWidth={260} delay={300}>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); o.run(); }}
                                      style={{ ...SEG_BTN, borderLeft: i > 0 ? '1px solid var(--dp-border-subtle)' : 'none' }}
                                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13,174,255,0.12)'; e.currentTarget.style.color = '#0DAEFF'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                                    >
                                      {o.icon} {o.label}
                                    </button>
                                  </DarkTooltip>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })
            )}
          </div>

          {/* Pagination */}
          {queue.length > ITEMS_PER_PAGE && (
            <div style={{
              display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px',
              padding: '16px 0',
            }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px', borderRadius: 'var(--dp-radius-sm, 8px)',
                  border: '1px solid var(--dp-border-subtle)', background: 'transparent',
                  color: currentPage === 1 ? 'var(--dp-text-tertiary)' : 'var(--dp-text-primary)',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1,
                  display: 'flex', alignItems: 'center',
                }}
              >
                <ChevronDown size={16} style={{ transform: 'rotate(90deg)' }} />
              </button>
              <span style={{ fontSize: '13px', color: 'var(--dp-text-primary)', fontWeight: 500, minWidth: '80px', textAlign: 'center' }}>
                {currentPage} de {Math.ceil(queue.length / ITEMS_PER_PAGE)}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(queue.length / ITEMS_PER_PAGE), p + 1))}
                disabled={currentPage >= Math.ceil(queue.length / ITEMS_PER_PAGE)}
                style={{
                  padding: '8px 12px', borderRadius: 'var(--dp-radius-sm, 8px)',
                  border: '1px solid var(--dp-border-subtle)', background: 'transparent',
                  color: currentPage >= Math.ceil(queue.length / ITEMS_PER_PAGE) ? 'var(--dp-text-tertiary)' : 'var(--dp-text-primary)',
                  cursor: currentPage >= Math.ceil(queue.length / ITEMS_PER_PAGE) ? 'not-allowed' : 'pointer',
                  opacity: currentPage >= Math.ceil(queue.length / ITEMS_PER_PAGE) ? 0.5 : 1,
                  display: 'flex', alignItems: 'center',
                }}
              >
                <ChevronDown size={16} style={{ transform: 'rotate(-90deg)' }} />
              </button>
            </div>
          )}
          </>
          )}
          </div>
          </div>
        </div>
        </div>

        {/* Hidden audio element */}
        <audio
          ref={audioRef}
          onEnded={() => { setIsPlaying(false); setPlayingItemId(null); setPlayerCurrentTime(0); vp.updateExternal({ isPlaying: false, currentTime: 0 }); }}
          onPause={() => vp.updateExternal({ isPlaying: false })}
          onPlay={() => vp.updateExternal({ isPlaying: true })}
          onTimeUpdate={(e) => { setPlayerCurrentTime(e.target.currentTime); vp.updateExternal({ currentTime: e.target.currentTime }); }}
          onLoadedMetadata={(e) => {
            const dur = e.target.duration;
            setPlayerDuration(dur);
            vp.updateExternal({ duration: dur });
            // Update queue item duration if missing (client-side fallback)
            if (playingItemId && dur && isFinite(dur)) {
              setQueue(prev => prev.map(q =>
                q.id === playingItemId && !q.duration
                  ? { ...q, duration: dur }
                  : q
              ));
            }
          }}
        />
      </main>

      {/* Modal de texto do áudio */}
      <AudioTextModal
        open={textModalOpen}
        onOpenChange={setTextModalOpen}
        item={textModalItem}
      />

      {/* Editor de SRT Tempo */}
      <SrtEditorModal
        open={srtEditorOpen}
        onOpenChange={setSrtEditorOpen}
        item={srtEditorItem}
      />

      {/* FASE 2: Biblioteca de Vozes (modal expandido com filtros faceted +
          dropdown inline de previews por idioma dentro de cada card). */}
      {libraryModalOpen && (
        <VoiceLibraryModal
          voices={voices}
          favorites={favorites}
          onSelectVoice={(v) => {
            setSelectedVoice(v.id);
            setLibraryModalOpen(false);
          }}
          onToggleFavorite={toggleFavorite}
          onClose={() => setLibraryModalOpen(false)}
          renderAvatar={(v, size = 36) => (
            <VoiceAvatar
              voice={v}
              size={size}
              isPreviewing={previewingVoiceId === v.id}
              onPlayClick={getPreviewUrl(v) ? () => toggleVoicePreview(v) : undefined}
            />
          )}
        />
      )}

      {/* Multivoz: modal de troca de voz (substitui todas as ocorrências do slug no textarea) */}
      {/* ════════ MODAL: CRIAR/EDITAR VARIAÇÃO DE VOZ ════════ */}
      {variationModal && (
        <VoiceVariationModal
          mode={variationModal.mode}
          originalName={variationModal.mode === 'edit'
            ? (variationModal.variation.original_name || '')
            : (variationModal.voice?.name || '')}
          initialName={variationModal.mode === 'edit' ? variationModal.variation.custom_name : ''}
          onSave={saveVariation}
          onClose={() => setVariationModal(null)}
        />
      )}

      {swapVoiceOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            padding: '16px',
          }}
          onClick={() => { setSwapVoiceOpen(false); setSwapTargetSlug(null); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--dp-bg-card, #1a1a2e)',
              border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.1))',
              borderRadius: '16px',
              width: '100%',
              maxWidth: 460,
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: 'var(--dp-font)',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 18px 10px',
              borderBottom: '1px solid var(--dp-border-subtle, rgba(255,255,255,0.06))',
            }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>
                Trocar voz: <span style={{ color: 'var(--dp-blue-500)' }}>[{swapTargetSlug}]</span>
              </h3>
              <button
                type="button"
                onClick={() => { setSwapVoiceOpen(false); setSwapTargetSlug(null); }}
                style={{
                  background: 'transparent', border: 'none', color: 'var(--dp-text-tertiary)',
                  cursor: 'pointer', padding: '4px', display: 'flex', borderRadius: '6px',
                }}
                aria-label="Fechar"
              >
                <X size={16} />
              </button>
            </div>
            {/* Search + filtros pills (mesmo padrão da biblioteca lateral) */}
            <div style={{ padding: '10px 14px 8px' }}>
              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--dp-text-tertiary)' }} />
                <input
                  type="text"
                  value={swapSearch}
                  onChange={(e) => setSwapSearch(e.target.value)}
                  placeholder={t('audio.search_voice')}
                  autoFocus
                  style={{
                    width: '100%', padding: '8px 12px 8px 32px',
                    background: 'var(--dp-bg-input, var(--dp-bg-primary))',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: 'var(--dp-radius-sm, 8px)', color: 'var(--dp-text-primary)',
                    fontFamily: 'var(--dp-font)', fontSize: '12px', outline: 'none',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                {[
                  { id: 'all', label: t('audio.filter_all') },
                  { id: 'favorites', label: '♡' },
                  { id: 'female', label: 'F' },
                  { id: 'male', label: 'M' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSwapFilter(tab.id)}
                    style={{
                      flex: 1,
                      padding: '6px 0',
                      borderRadius: '9999px',
                      border: swapFilter === tab.id ? '1px solid rgba(13,174,255,0.4)' : '1px solid var(--dp-border-subtle)',
                      background: swapFilter === tab.id
                        ? 'linear-gradient(135deg, rgba(13,174,255,0.12), rgba(13,174,255,0.04))'
                        : 'transparent',
                      color: swapFilter === tab.id ? 'var(--dp-blue-500)' : 'var(--dp-text-tertiary)',
                      fontSize: '11px',
                      fontWeight: swapFilter === tab.id ? 600 : 500,
                      cursor: 'pointer',
                      transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
                      fontFamily: 'var(--dp-font)',
                      textAlign: 'center',
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Voice list — reutiliza renderVoiceVisual da biblioteca lateral (com showSlug=true) */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px 14px' }} className="ag-scrollbar">
              {swapVoiceList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 15px', color: 'var(--dp-text-tertiary)' }}>
                  <Heart size={32} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
                  <p style={{ fontSize: '12px' }}>{swapFilter === 'favorites' ? t('audio.no_favorites') : t('audio.no_voices')}</p>
                </div>
              ) : (
                swapVoiceList.map((voice) => {
                  const isPreviewing = previewingVoiceId === voice.id;
                  return (
                    <div
                      key={voice.id}
                      className="voice-item-ag"
                      onClick={() => performSwap(swapTargetSlug, voice.slug)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 10px',
                        borderRadius: 'var(--dp-radius-sm, 8px)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        marginBottom: '1px',
                        border: '1px solid transparent',
                        background: 'transparent',
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {renderVoiceVisual(voice, {
                        isSelected: false,
                        isPreviewing,
                        onPlayClick: voice.preview_url ? () => toggleVoicePreview(voice) : undefined,
                        showSlug: true,
                      })}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════ STYLES ════════ */}
      <style>{`
        @keyframes agFadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes agSlideIn {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes agPopupIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes agPulseRing {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.08); opacity: 0.2; }
        }
        .ag-pulse-ring {
          animation: agPulseRing 2.5s ease-in-out infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        /* Pulse usado no status "▶ ATIVO" dos cards de resultado em playback */
        @keyframes statusPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
        .status-active {
          animation: statusPulse 1.5s ease-in-out infinite;
        }

        /* Voice card hover — show play overlay on avatar and favorite button */
        .voice-item-ag:hover .voice-avatar-ag .play-overlay-ag { opacity: 1 !important; }
        .voice-item-ag:hover .voice-avatar-ag { transform: scale(1.05); }
        .voice-item-ag:hover .fav-btn-ag { opacity: 1 !important; }
        .play-overlay-ag.visible { opacity: 1 !important; }

        /* Custom scrollbar */
        .ag-scrollbar::-webkit-scrollbar { width: 4px; }
        .ag-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .ag-scrollbar::-webkit-scrollbar-thumb { background: rgba(13, 174, 255, 0.2); border-radius: 2px; }

        /* Custom slider */
        /* Sliders estilo Fish: barra cheia (preenchimento azul via inline) +
           handle vertical fino branco com DROP SHADOW limpa (sem glow azul). */
        .ag-slider {
          -webkit-appearance: none;
          width: 100%;
          height: 6px;
          background: var(--dp-border-medium);
          border-radius: 9999px;
          outline: none;
          cursor: ew-resize;
        }
        .ag-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 5px;
          height: 16px;
          border-radius: 3px;
          background: #ffffff;
          cursor: ew-resize;
          box-shadow: 0 1px 4px rgba(0,0,0,0.45);
          transition: transform 0.12s ease;
        }
        .ag-slider::-webkit-slider-thumb:hover { transform: scaleY(1.15); }
        .ag-slider::-moz-range-thumb {
          width: 5px;
          height: 16px;
          border-radius: 3px;
          background: #ffffff;
          cursor: ew-resize;
          border: none;
          box-shadow: 0 1px 4px rgba(0,0,0,0.45);
        }

        /* Dual-range slider (intervalo VEO) — dois inputs sobrepostos; só o thumb captura clique */
        .ag-range-dual {
          -webkit-appearance: none;
          appearance: none;
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 16px;
          margin: 0;
          background: transparent;
          pointer-events: none;
          outline: none;
        }
        .ag-range-dual::-webkit-slider-runnable-track { background: transparent; border: none; }
        .ag-range-dual::-moz-range-track { background: transparent; border: none; }
        .ag-range-dual::-webkit-slider-thumb {
          -webkit-appearance: none;
          pointer-events: auto;
          width: 5px;
          height: 16px;
          border-radius: 3px;
          background: #ffffff;
          cursor: ew-resize;
          box-shadow: 0 1px 4px rgba(0,0,0,0.45);
          transition: transform 0.12s ease;
        }
        .ag-range-dual::-webkit-slider-thumb:hover { transform: scaleY(1.15); }
        .ag-range-dual::-moz-range-thumb {
          pointer-events: auto;
          width: 5px;
          height: 16px;
          border-radius: 3px;
          background: #ffffff;
          cursor: ew-resize;
          border: none;
          box-shadow: 0 1px 4px rgba(0,0,0,0.45);
        }

        /* Text editor focus glow */
        .ag-text-editor:focus-within {
          border-color: rgba(13,174,255,0.20) !important;
          box-shadow: 0 0 0 1px rgba(13,174,255,0.20);
        }

        /* Responsive — container queries (responde ao espaço REAL, não viewport) */
        @container main-content (max-width: 900px) {
          .audio-layout-grid {
            grid-template-columns: 210px 1fr !important;
          }
        }
        @container main-content (max-width: 600px) {
          .audio-layout-grid {
            grid-template-columns: 1fr !important;
          }
        }
        /* Layout opcional "2 colunas": biblioteca + editor em cima; o painel
           (config/resultados) desce e ocupa toda a largura do container, embaixo.
           Só em telas largas — abaixo de 900px o colapso normal acima já manda. */
        @container main-content (min-width: 901px) {
          .audio-layout-grid.ag-two-col {
            grid-template-columns: 300px minmax(0, 1fr) !important;
          }
          .audio-layout-grid.ag-two-col > :nth-child(3) {
            grid-column: 1 / -1;
          }
          /* Painel embaixo (full width): config COMPACTA em grade horizontal +
             resultados em grade. Headers/divisores/reset ocupam a linha toda. */
          .ag-two-col .ag-settings {
            display: grid !important;
            grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
            gap: 8px 14px;
            align-items: start;
            overflow: visible !important;
            flex: none !important;
          }
          .ag-two-col .ag-settings > * { margin-top: 0 !important; margin-bottom: 0 !important; }
          .ag-two-col .ag-settings > .ag-cfg-full { grid-column: 1 / -1; }
          /* Resultados: UMA LINHA por item (coluna vertical full-width), crescendo
             com o conteúdo — a página rola, sem scroll interno. */
          .ag-two-col .ag-results-list {
            overflow: visible !important;
            flex: none !important;
          }
        }
      `}</style>
    </div>
  );
}

// Envolve a página no player de preview COMPARTILHADO + a barra inferior estilo
// ElevenLabs. O modal de vozes é renderizado dentro de Inner -> usa o mesmo player.
export default function AudioGeneratorPage(props) {
  return (
    <VoicePreviewProvider>
      <AudioGeneratorPageInner {...props} />
      <VoicePreviewBar />
    </VoicePreviewProvider>
  );
}
